import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { profilePath, PROFILE_TAB_FROM_SLUG, PWA_PROFILE_TAB_FROM_SLUG } from '../../routes/slugs';
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import ProfileImage from '../common/ProfileImage';
import CircularProgress from '../common/CircularProgress';
import { useAuth } from '../../contexts/AuthContext';
import { usePsychologistContext } from '../../contexts/PsychologistContext';
import { useRankingTransition } from '../../hooks/useRankingTransition';
import apiCoreAdapter from '../../hooks/apiCoreAdapter';
import { getPsychologists } from '../../hooks/api/psychologist/list';
import { toISODateOnly } from '../../utils/date';
import { normalizeForCompare } from '../../utils/normalizeForCompare';
import {
  VACATION_BLOCK_BRACKETS,
  VACATION_BLOCK_TABLE_HEADERS,
  VACATION_BLOCK_ILLUSTRATIVE_EXAMPLES,
  getVacationBracketIndex,
} from '../../utils/vacationBlockBracket';
import { toastUtils } from '../../hooks/useToast';
import { checkAndMarkOnboardingComplete, markTourCompleteOnServer, STEP_KEYS, getSavedStep, saveStep, clearStep, waitForOnboardingSync } from '../../utils/onboarding';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Pencil, FileText, ArrowUpRight, Upload, ChevronLeft, X } from 'lucide-react';
import { ProfileGeneralTab } from './profile/ProfileGeneralTab';
import TimezoneModal from './profile/TimezoneModal';
import { ProfileAttentionTab } from './profile/ProfileAttentionTab';
import { ProfileVacationsTab } from './profile/ProfileVacationsTab';
import { OVConditionsModal } from './profile/OVConditionsModal';
import ProfileMetricsTab from './profile/ProfileMetricsTab';
import TherapeuticStyleTab from './profile/TherapeuticStyleTab';
import { PwaNotificaciones } from '../../pwa/PwaNotificaciones';
import { useIsPwaApp } from '../../pwa/PwaModeContext';
import { getTherapeuticStyleState, TherapeuticStyleState } from '../../hooks/api/therapeuticStyle';
import { ValidationResultPanel } from './profile/certification/ValidationResultPanel';
import { useValidateServiceCertification } from '../../hooks/useValidateServiceCertification';
import { getClinicalServiceId } from '../../utils/clinicalService';
import { http } from '../../hooks/api/http';
import { CertificationValidationStatus } from '../../types/certificationValidation';
import { invalidateSchedulesCache, invalidateSessionsCache } from './calendar/data';
import { markTimezoneAgendaBannerPending } from '../../utils/timezoneAgendaNotice';
import { BA_TIMEZONE } from '../../utils/timezone';
import { useContactChannel } from '../../hooks/useContactChannel';
import { ContactoDirectoModal } from '../common/ContactoDirectoModal';
import { CALENDAR_BLOCKADE_INFO, CALENDAR_BLOCKADE_REASONS, CalendarBlockadeReason } from '../../utils/calendarBlockadeInfo';

import { activityTracker } from '../../utils/activityTracker';
interface PsychologistProfileProps {
  name?: string;
  lastName?: string;
  services?: Array<{ id: string; service: { id: string; name: string; description?: string } }>;
  servicesLoading?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

// Estructura local para datos de perfil
type ProfileData = {
  name: string;
  lastName: string;
  birthDate: string;
  phone: string;
  profileImage: string | null;
};

// Ítem de servicio extendido con estados de certificación.
// certStatus solo persiste 'rejected_escalated' (ver CERT_REJECTED_KEY) — 'validating' y
// 'needs_additional_document' son transitorios y viven en useValidateServiceCertification,
// no en este estado (se combinan solo para renderizar el chip mientras el modal está abierto).
type ServiceItem = { name: string; active: boolean; certStatus?: 'rejected_escalated' };

// localStorage key para el único estado de certificación que persiste entre sesiones:
// rejected_escalated ("En revisión" — la IA no pudo validar ni con documentación adicional).
const CERT_REJECTED_KEY = 'psi_cert_rejected_services';
// Legacy: solicitudes de certificado pendientes de revisión manual (flujo previo a la IA).
// Ya no se escribe en confirmSave() (ver 3a), pero se sigue leyendo para no repetirle el
// pedido de documentación a una psicóloga con una solicitud vieja todavía pendiente.
const CERT_PENDING_KEY = 'psi_cert_pending_services';

/**
 * Lee en vivo los servicios con solicitud de certificado ya pendiente.
 * Se relee desde localStorage (no desde el estado de React) en el momento de guardar,
 * para evitar solicitudes/mails duplicados si el estado en memoria quedó desactualizado
 * (ej: otra pestaña o sesión ya generó la solicitud pendiente).
 */
function readPendingCertServices(): string[] {
  try {
    return JSON.parse(localStorage.getItem(CERT_PENDING_KEY) || '[]') as string[];
  } catch {
    return [];
  }
}

// Datos de mockup para información profesional. `themes`/`approaches` NO van
// acá — a diferencia de `services` (siempre son los mismos 8, ver
// baseServices más abajo), su catálogo es dinámico (ver
// psychologistPreferencesCatalog) y arranca vacío hasta que cargue.
const mockProfessionalData = {
  joinDate: '2 de septiembre de 2024',
  services: [
    { name: 'Terapia individual', active: true },
    { name: 'Terapia familiar', active: false },
    { name: 'Terapia de pareja', active: true },
    { name: 'Orientación a padres', active: false },
    { name: 'Orientación vocacional', active: true },
    { name: 'Supervisiones', active: false },
    { name: 'Talleres/charlas', active: false }
  ],
  themes: [] as Array<{ name: string; active: boolean }>,
  approaches: [] as Array<{ name: string; active: boolean }>,
};

export const PsychologistProfile: React.FC<PsychologistProfileProps> = ({ name, lastName, services, servicesLoading, onDirtyChange }) => {
  const { user, psychologistId } = useAuth();
  const { psychologist, loadPsychologist, setPsychologist } = usePsychologistContext();
  // El fetch de /profile corre en un efecto que solo se monta una vez (deps []); usamos un ref
  // para leer siempre el `psychologist` más reciente del contexto dentro de ese callback async,
  // en vez del valor potencialmente stale capturado en el closure del mount.
  const psychologistRef = useRef(psychologist);
  psychologistRef.current = psychologist;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: tabSlug } = useParams<{ tab?: string }>();
  // App mobile: suma la sección Notificaciones y NO tiene onboarding guiado (solo web).
  const isPwaApp = useIsPwaApp();
  // Ocultar la card de "Cantidad de pacientes" en Sobre tu atención para NZ, N, y O que viene
  // de NZ o de N (mismos criterios de segmentación que "Tu estadía en Psi Mammoliti").
  const { rankingActual: attentionRankingActual, rankingAnterior: attentionRankingAnterior } = useRankingTransition();
  // payment_method=range (NZ/O-desde-NZ) o segmento nuevo: canal de administración; ver useContactChannel.
  const contactChannel = useContactChannel();
  const hidePatientLimitCard = attentionRankingActual === 'NZ'
    || attentionRankingActual === 'N'
    || (attentionRankingActual === 'O' && (attentionRankingAnterior === 'NZ' || attentionRankingAnterior === 'N'));
  // Si el psicólogo está cacheado sin processLimit (storage viejo), recargarlo
  useEffect(() => {
    const psiId = psychologistId || psychologist?.id;
    if (psiId && typeof psychologist?.processLimit !== 'number') {
      loadPsychologist(psiId).catch(() => { /* silencioso */ });
    }
  }, [psychologistId, psychologist?.id, psychologist?.processLimit, loadPsychologist]);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [preferencesCatalogLoading, setPreferencesCatalogLoading] = useState<boolean>(true);
  // Estilo terapéutico (preguntas de escenario C-NIP) — `null` mientras carga o si falló;
  // `questions: []` es un resultado válido (rollout restringido, o quieroserpaciente sin
  // preguntas activas) y significa "tab oculta", no "tab vacía". Ver TherapeuticStyleTab.
  const [therapeuticStyleState, setTherapeuticStyleState] = useState<TherapeuticStyleState | null>(null);
  const [therapeuticStyleLoading, setTherapeuticStyleLoading] = useState<boolean>(true);
  // Cualquier otro filtro que el equipo habilite para psi-platform además de
  // Temáticas/Enfoques (topic_preference/specialization). `writable` decide
  // si es clickeable: viene de psiplatform-be, que a su vez lo resuelve
  // contra el enum ClinicalPreferenceType de api-core (los únicos types que
  // el PUT /profile sabe persistir). Un filtro cuyo type todavía no está en
  // ese enum se muestra igual, pero de solo lectura.
  const [otherPreferenceGroups, setOtherPreferenceGroups] = useState<
    Array<{ filterKey: string; label: string; type: string; writable: boolean; items: Array<{ name: string; active: boolean }> }>
  >([]);
  // Título de las secciones Temáticas/Enfoques — también viene del catálogo
  // (label/psiPlatformLabel configurado en quieroserpaciente), no hardcodeado.
  // Default = el nombre de siempre, para no mostrar la sección sin título
  // mientras carga o si el catálogo falla.
  const [dedicatedSectionLabels, setDedicatedSectionLabels] = useState({ themes: 'Temáticas', approaches: 'Enfoques' });
  const [originalProfile, setOriginalProfile] = useState<{
    name?: string;
    surname?: string;
    birthdate?: string; // ISO full
    phone?: string;
    services: string[]; // normalized Spanish labels
    topics: string[];
    specializations: string[];
    // Baseline (nombres activos al cargar) de cualquier otro filtro writable
    // habilitado para psi-platform, por filterKey — para diffear al guardar.
    otherPreferences: Record<string, string[]>;
  }>({ services: [], topics: [], specializations: [], otherPreferences: {} });
  
  // Función para parsear el nombre del usuario de Google
  const parseUserName = useCallback(() => {
    if (!user?.name) {
      return { firstName: name ?? '', lastName: lastName ?? '' };
    }

    // Separar el nombre completo en partes
    const nameParts = user.name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return { firstName: nameParts[0], lastName: lastName ?? '' };
    }
    
    // Si hay más de una parte, tomar la primera como nombre y el resto como apellido
    const firstName = nameParts[0];
    const lastNameParts = nameParts.slice(1).join(' ');
    
    return { firstName, lastName: lastNameParts };
  }, [user?.name, name, lastName]);

  const initialNames = parseUserName();
  const googlePicture: string | null = (() => {
    try { return JSON.parse(localStorage.getItem('google_user') || '{}')?.picture || null; } catch { return null; }
  })();
  const [profileTimezone, setProfileTimezone] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData>({
    name: initialNames.firstName || '',
    lastName: initialNames.lastName || '',
    birthDate: '',
    phone: '',
    profileImage: googlePicture,
  });

  // El nombre para el chequeo de titularidad del system prompt (regla 7) lo deriva
  // el backend del token de la sesión — no se manda desde el cliente.
  const certValidation = useValidateServiceCertification();
  const [certAdditionalFilesMap, setCertAdditionalFilesMap] = useState<Record<string, File | null>>({});

  // Mapear los servicios reales al formato esperado
  const mappedServices = services && services.length > 0
    ? services.map(s => ({ name: s.service.name, active: true }))
    : mockProfessionalData.services;
  
  const [professionalData, setProfessionalData] = useState({
    ...mockProfessionalData,
    services: mappedServices,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [savePopupStep, setSavePopupStep] = useState<1 | 2 | 3>(1);
  const [consentChecked, setConsentChecked] = useState(false);
  type DiffCategory = { added: string[]; removed: string[] };
  type PatientLimitDiff = { from: number | null; to: number | null };
  const [changesDiff, setChangesDiff] = useState<{ services: DiffCategory; themes: DiffCategory; approaches: DiffCategory; other: Record<string, DiffCategory & { label: string }>; patientLimit: PatientLimitDiff }>({
    services: { added: [], removed: [] },
    themes: { added: [], removed: [] },
    approaches: { added: [], removed: [] },
    other: {},
    patientLimit: { from: null, to: null },
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [, setCertRequestedThisFlow] = useState(false);
  const [pendingCertServices, setPendingCertServices] = useState<string[]>([]);
  const [certFilesMap, setCertFilesMap] = useState<Record<string, File | null>>({});
  const [showOVModal, setShowOVModal] = useState(false);
  const [pendingOVIndex, setPendingOVIndex] = useState<number | null>(null);

  // Vacation block: undefined = loading, false = no active block, null = blocked without end date, string = blocked until date
  const [activeVacationEnd, setActiveVacationEnd] = useState<string | null | false | undefined>(undefined);
  const [activeVacationDays, setActiveVacationDays] = useState<number | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showCalendarBlockModal, setShowCalendarBlockModal] = useState(false);
  const [showContactoDirecto, setShowContactoDirecto] = useState(false);

  // Calendar blockades (tope, sin horarios, situaciones personales)
  const [activeBlockade, setActiveBlockade] = useState<{ reason: string; to: string | null } | false | undefined>(undefined);
  const [processLimitPrefered, setProcessLimitPrefered] = useState<number | null>(null);
  // Draft local para edición del tope preferido. null = aún no editado y server tampoco tiene valor.
  const [processLimitPreferredDraft, setProcessLimitPreferredDraft] = useState<number | null>(null);
  // Cantidad de procesos activos (active + retention). null mientras carga / falla.
  const [currentAssignedProcesses, setCurrentAssignedProcesses] = useState<number | null>(null);

  // Notify parent when dirty state changes (incluye cambios del tope preferido de pacientes)
  useEffect(() => {
    const limitDirty = processLimitPreferredDraft !== null
      && processLimitPreferredDraft !== processLimitPrefered;
    onDirtyChange?.(hasChanges || limitDirty);
  }, [hasChanges, processLimitPreferredDraft, processLimitPrefered, onDirtyChange]);

  // Warn on browser tab close / refresh
  useEffect(() => {
    const limitDirty = processLimitPreferredDraft !== null
      && processLimitPreferredDraft !== processLimitPrefered;
    if (!hasChanges && !limitDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges, processLimitPreferredDraft, processLimitPrefered]);

  // Procesar el parámetro ?cert_rejected=ServiceName que llega cuando el admin rechaza un cert.
  // El backend incluye ese link en el email de rechazo que le envía a la psicóloga.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const certRejected = params.get('cert_rejected');
    if (!certRejected) return;
    const rejected: string[] = (() => { try { return JSON.parse(localStorage.getItem(CERT_REJECTED_KEY) || '[]') as string[]; } catch { return []; } })();
    if (!rejected.includes(certRejected)) {
      rejected.push(certRejected);
      localStorage.setItem(CERT_REJECTED_KEY, JSON.stringify(rejected));
    }
    // Limpiar solo el parámetro cert_rejected, preservando el resto de la query string
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('cert_rejected');
      return next;
    }, { replace: true });
    toastUtils.warning(`La documentación enviada para "${certRejected}" fue rechazada. Activá el servicio y subí nueva documentación.`);
  // Solo en montaje y cuando cambia location.search (navegación con ?cert_rejected=)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Archivos
  type FileEntry = { name: string; folder: string; size: number; updatedAt: string; publicUrl: string; objectPath: string };
  const [myFiles, setMyFiles] = useState<FileEntry[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [renamingFile, setRenamingFile] = useState<{ objectPath: string; value: string } | null>(null);
  const [renamingLoading, setRenamingLoading] = useState(false);
  const [isDocumentosModalOpen, setIsDocumentosModalOpen] = useState(false);
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useState(
    () => !!(location.state as { openTimezoneModal?: boolean } | null)?.openTimezoneModal
  );
  const [previewFile, setPreviewFile] = useState<FileEntry | null>(null);
  const validTabs = ['general', 'attention', 'vacations', 'metrics', 'therapeuticStyle', 'notifications'] as const;
  type ProfileTab = typeof validTabs[number];
  // Fuente de verdad: el slug de path (/profile/atencion|vacaciones|metricas). "metricas" se
  // puede matchear tanto vía /profile/:tab (bare) como vía /profile/metricas/:subtab (con
  // sub-tab) — por eso se chequea también el pathname, no solo el param `tab`.
  const isMetricsPath = location.pathname === '/profile/metricas' || location.pathname.startsWith('/profile/metricas/');
  const tabFromPath = (): ProfileTab | null => {
    if (isMetricsPath) return 'metrics';
    if (tabSlug) {
      // "Notificaciones" solo existe en la app mobile: en la web ese slug no abre nada.
      return PROFILE_TAB_FROM_SLUG[tabSlug] ?? (isPwaApp ? PWA_PROFILE_TAB_FROM_SLUG[tabSlug] : undefined) ?? null;
    }
    return null;
  };
  // Tab inicial: prioridad slug de path > ?tab= legacy (deep-link compartible) > location.state.openTab > general
  const initialTab: ProfileTab = (() => {
    const fromPath = tabFromPath();
    if (fromPath) return fromPath;
    const fromQuery = searchParams.get('tab') as ProfileTab | null;
    if (fromQuery && validTabs.includes(fromQuery) && (fromQuery !== 'notifications' || isPwaApp)) return fromQuery;
    const fromState = (location.state as { openTab?: string })?.openTab as ProfileTab | undefined;
    if (fromState && validTabs.includes(fromState)) return fromState;
    return 'general';
  })();
  const [activeTab, setActiveTabState] = useState<ProfileTab>(initialTab);
  // Sincroniza `activeTab` cuando el slug del path cambia externamente (link directo,
  // back/forward del navegador).
  useEffect(() => {
    const fromPath = tabFromPath();
    if (fromPath && fromPath !== activeTab) setActiveTabState(fromPath);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSlug, location.pathname]);
  // Link viejo ?tab=<tab> sin slug en el path → migrar (replace), preservando el resto de la query.
  useEffect(() => {
    if (tabFromPath()) return;
    const tabParam = searchParams.get('tab') as ProfileTab | null;
    if (tabParam && validTabs.includes(tabParam) && tabParam !== 'general') {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      const q = next.toString();
      navigate(`${profilePath(tabParam)}${q ? `?${q}` : ''}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSlug, location.pathname]);
  // Wrapper: además de actualizar el estado, navega al slug de la tab (replace, sin ensuciar historial)
  const setActiveTab = useCallback((tab: ProfileTab) => {
    setActiveTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    const q = next.toString();
    navigate(`${profilePath(tab)}${q ? `?${q}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  const loadFiles = useCallback(async () => {
    if (!profileData.name || !profileData.lastName) return;
    setLoadingFiles(true);
    try {
      const result = await apiCoreAdapter.listFiles(`${profileData.name} ${profileData.lastName}`);
      setMyFiles(result.files || []);
    } catch {
      setMyFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [profileData.name, profileData.lastName]);

  useEffect(() => {
    if (profileLoaded && profileData.name && profileData.lastName) loadFiles();
  }, [profileLoaded, profileData.name, profileData.lastName, loadFiles]);

  const handleDocUpload = async (file: File) => {
    setUploadingDoc(true);
    try {
      const contentType = file.type || 'application/octet-stream';
      const { url, headers } = await apiCoreAdapter.getSignedUrlForUpload({
        filename: file.name,
        contentType,
        size: file.size,
        category: 'documents',
      });
      await fetch(url, { method: 'PUT', headers: headers || {}, body: file });
      toastUtils.success('Archivo subido correctamente');
      await loadFiles();
    } catch {
      toastUtils.error('No se pudo subir el archivo');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRenameFile = async () => {
    if (!renamingFile) return;
    const newName = renamingFile.value.trim();
    if (!newName) return;
    setRenamingLoading(true);
    try {
      await apiCoreAdapter.renameFile(renamingFile.objectPath, newName);
      toastUtils.success('Archivo renombrado');
      setRenamingFile(null);
      await loadFiles();
    } catch {
      toastUtils.error('No se pudo renombrar el archivo');
    } finally {
      setRenamingLoading(false);
    }
  };

  // (Botón de repetir guía usa directamente setRunProfileTour(true))

  // Onboarding - Perfil
  const [profileTourKey, setProfileTourKey] = useState<number>(0);
  const [runProfileTour, setRunProfileTour] = useState<boolean>(false);
  const [canSkipTour, setCanSkipTour] = useState(false);
  const [profileStepIndex, setProfileStepIndex] = useState<number>(() => {
    const saved = getSavedStep(STEP_KEYS.profile);
    return saved >= 0 ? saved : 0;
  });
  const isNZTour = psychologist?.rankingCategory === 'NZ';
  const profileSteps: Step[] = [
    {
      target: '#profile-personal-card',
      content: isNZTour
        ? 'Acá tenés tu sala virtual — siempre que tengas sesión, ingresás por este mismo link.'
        : 'Aquí está tu avatar y saludo de bienvenida. Tu foto de perfil y nombre se actualizan automáticamente desde tu cuenta.',
      disableBeacon: true,
      disableScrolling: false,
      spotlightPadding: 6,
      floaterProps: { offset: 12 },
    },
    {
      target: '#profile-tabs-card',
      content: isNZTour ? (
        <div className="text-left">
          <p className="mb-2">En &quot;Tu perfil&quot; encontrarás:</p>
          <ul className="list-disc ml-4 space-y-1.5">
            <li><strong>Información general:</strong> son tus datos personales y profesionales que tenemos registrados.</li>
            <li><strong>Sobre tu atención:</strong> acá están todas las preferencias (servicios, temáticas, etc.) para la atención clínica.</li>
            <li><strong>Vacaciones y Días Off:</strong> acá podrás pedir días off, y también visualizar todos los que hayas solicitado.</li>
            <li>
              <strong>Tu estadía en Psi Mammoliti:</strong> contiene 2 secciones principales:
              <ul className="list-disc ml-4 mt-1 space-y-1">
                <li><strong>Camino inicial:</strong> una guía informativa, con recursos que aparecen a medida que vas avanzando en nuestra plataforma.</li>
                <li><strong>Liquidaciones:</strong> todo lo relacionado a los pagos.</li>
              </ul>
            </li>
          </ul>
        </div>
      ) : 'Este es tu panel de perfil. Tiene cuatro pestañas: Información general (tus datos personales y accesos), Sobre tu atención (servicios, temáticas y enfoques), Vacaciones y Días off, y Tu estadía en Psi Mammoliti.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'top',
      floaterProps: { offset: 12 },
    },
    {
      target: '#profile-attention-section',
      content: 'En "Sobre tu atención" podés configurar servicios, temáticas y enfoques. Esta selección impacta directamente en los pacientes que recibís, por lo que es importante elegir responsablemente.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'bottom',
      floaterProps: { offset: 12 },
    },
  ];

  type JoyrideTooltipProps = {
    index: number;
    size: number;
    step?: { content?: React.ReactNode };
    backProps: Record<string, unknown>;
    closeProps: Record<string, unknown>;
    primaryProps: Record<string, unknown>;
    skipProps: Record<string, unknown>;
    tooltipProps: Record<string, unknown>;
    continuous?: boolean;
  };
  const ProfileJoyrideTooltip: React.FC<JoyrideTooltipProps> = ({
    index,
    size: _size,
    step,
    backProps,
    primaryProps,
    skipProps,
    tooltipProps,
    continuous: _continuous,
  }) => {
    const total = 3;
    const current = (Number(index) || 0) + 1;
    const isLast = current >= total;
    const nextLabel = isLast ? 'Entendido' : `Continuar (${current} paso de ${total})`;

    return (
      <div
        {...tooltipProps}
        style={{
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          maxWidth: 420,
          padding: '1rem 1rem 0.75rem 1rem',
          color: '#111827',
        }}
      >
        <div style={{ marginBottom: '0.75rem' }}>{step?.content}</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            paddingTop: '0.25rem',
          }}
        >
          {index > 0 && (
            <button {...backProps} className="btn-outline" style={{ height: 32 }}>
              Atrás
            </button>
          )}
            <button
              {...primaryProps}
              className="btn-primary"
              style={{ 
                height: 32, 
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 14px',
                whiteSpace: 'nowrap',
                lineHeight: '32px'
              }}
            >
              {nextLabel}
            </button>
        </div>
        {canSkipTour && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "0.5rem" }}>
            <button {...skipProps} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "0.9rem", fontFamily: "inherit", fontWeight: 700 }}>Saltar</button>
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ProfileTooltipAny = ProfileJoyrideTooltip as any;

  useEffect(() => {
    if (isPwaApp) return; // onboarding guiado: solo web
    let cancelled = false;
    waitForOnboardingSync().then(() => {
      if (cancelled) return;
      try {
        const done = localStorage.getItem('psi-profile-tour-done');
        if (!done) {
          setCanSkipTour(false);
          const saved = getSavedStep(STEP_KEYS.profile);
          const startAt = saved >= 0 ? saved : 0;
          setTimeout(() => { if (!cancelled) { setProfileStepIndex(startAt); setRunProfileTour(true); } }, 300);
        } else {
          setCanSkipTour(true);
        }
      } catch { /* noop */ }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistir paso del tour de perfil
  useEffect(() => {
    if (runProfileTour) saveStep(STEP_KEYS.profile, profileStepIndex);
  }, [profileStepIndex, runProfileTour]);

  const onProfileTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & { action?: string };
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunProfileTour(false);
      clearStep(STEP_KEYS.profile);
      setActiveTab('general');
      const wasAlreadyDone = localStorage.getItem('psi-profile-tour-done') === '1';
      try { localStorage.setItem('psi-profile-tour-done', '1'); } catch { /* noop */ }
      // Le avisa a quien esté esperando (ej. el popup de "nuevos recursos" de Camino Inicial)
      // que este tour terminó, para no superponer dos overlays de bienvenida a la vez.
      window.dispatchEvent(new Event('psi-profile-tour-finished'));
      checkAndMarkOnboardingComplete();
      if (!wasAlreadyDone) markTourCompleteOnServer('psi-profile-tour-done');
      return;
    }
    if (type === 'step:after') {
      if (action === 'next') {
        const nextIndex = (index ?? 0) + 1;
        // Paso 2 (índice 2): cambiar a la pestaña "Sobre tu atención" para mostrar #profile-attention-section
        if (nextIndex === 2) setActiveTab('attention');
        setProfileStepIndex(nextIndex);
      }
      if (action === 'prev') {
        const prevIndex = Math.max(0, (index ?? 0) - 1);
        // Al volver al paso 1 (índice 1): volver a la pestaña general
        if (prevIndex < 2) setActiveTab('general');
        setProfileStepIndex(prevIndex);
      }
    }
  };

  // Onboarding - Sobre tu atención (recorrido propio de la sub-pestaña, además del
  // vistazo genérico que ya da el paso 3 del tour de Perfil). Sincroniza contra
  // `attention_onboarding` en platform_preferences, igual que los otros tours secundarios
  // (día específico, bloqueo, tipo de reprogramación) — ver utils/onboarding.ts.
  const [attentionTourKey, setAttentionTourKey] = useState<number>(0);
  const [runAttentionTour, setRunAttentionTour] = useState<boolean>(false);
  const [attentionStepIndex, setAttentionStepIndex] = useState<number>(0);
  const attentionSteps: Step[] = [
    {
      target: '#attention-tour-services',
      content: 'Acá activás los servicios que ofrecés (Individual, Pareja, Familia, etc.). Elegí solo los que realmente brindás — algunas opciones pueden requerir certificación antes de quedar visibles.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'bottom',
    },
    {
      target: '#attention-tour-themes',
      content: `"${dedicatedSectionLabels.themes}" son tus áreas de experiencia clínica (ansiedad, duelo, trauma, etc.). "Desarrollo personal" es obligatoria: podés guardar tus preferencias con solo esa activada.`,
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'bottom',
    },
    {
      target: '#attention-tour-approaches',
      content: `"${dedicatedSectionLabels.approaches}" son los marcos teóricos que usás en tu práctica (TCC, Gestalt, EMDR, etc.). Elegí al menos uno que describa tu forma de trabajar habitual.`,
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'top',
    },
    {
      target: '#attention-tour-save',
      content: 'Cuando termines de activar o desactivar opciones, hacé click en "Guardar Cambios" para aplicarlas. Esta selección impacta directamente en los pacientes que te asignamos, así que es importante mantenerla al día.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'top',
    },
  ];

  const AttentionJoyrideTooltip: React.FC<JoyrideTooltipProps> = ({ index, step, backProps, primaryProps, tooltipProps }) => {
    const total = attentionSteps.length;
    const current = (Number(index) || 0) + 1;
    const isLast = current >= total;
    return (
      <div
        {...tooltipProps}
        style={{ background: '#fff', borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', maxWidth: 420, padding: '1rem 1rem 0.75rem 1rem', color: '#111827' }}
      >
        <div style={{ marginBottom: '0.75rem' }}>{step?.content}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', paddingTop: '0.25rem' }}>
          {index > 0 && (
            <button {...backProps} className="btn-outline" style={{ height: 32 }}>
              Atrás
            </button>
          )}
          <button
            {...primaryProps}
            className="btn-primary"
            style={{ height: 32, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px', whiteSpace: 'nowrap', lineHeight: '32px' }}
          >
            {isLast ? 'Entendido' : `Continuar (${current} de ${total})`}
          </button>
        </div>
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AttentionTooltipAny = AttentionJoyrideTooltip as any;

  const onAttentionTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & { action?: string };
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunAttentionTour(false);
      try { localStorage.setItem('psi-attention-tour-done', '1'); } catch { /* noop */ }
      markTourCompleteOnServer('psi-attention-tour-done');
      checkAndMarkOnboardingComplete();
      return;
    }
    if (type === 'step:after') {
      if (action === 'next') setAttentionStepIndex((index ?? 0) + 1);
      if (action === 'prev') setAttentionStepIndex(Math.max(0, (index ?? 0) - 1));
    }
  };

  // Auto-arranca la primera vez que se entra a "Sobre tu atención", una vez que el tour
  // principal de Perfil ya terminó — evita superponer este recorrido con el paso 3 del
  // tour de Perfil, que ya de por sí muestra brevemente esta misma sección.
  useEffect(() => {
    if (isPwaApp || activeTab !== 'attention' || runProfileTour) return;
    try {
      if (localStorage.getItem('psi-profile-tour-done') !== '1') return;
      if (localStorage.getItem('psi-attention-tour-done') === '1') return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => {
      setAttentionStepIndex(0);
      setRunAttentionTour(true);
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, runProfileTour]);

  // Onboarding - Vacaciones y Días off (mismo patrón que "Sobre tu atención": tour propio
  // de la sub-pestaña, sincroniza contra `vacations_onboarding` en platform_preferences).
  const [vacationsTourKey, setVacationsTourKey] = useState<number>(0);
  const [runVacationsTour, setRunVacationsTour] = useState<boolean>(false);
  const [vacationsStepIndex, setVacationsStepIndex] = useState<number>(0);
  const vacationsSteps: Step[] = [
    {
      target: '#vacations-tour-inform-btn',
      content: 'Usá este botón para informar un período de vacaciones o días off — siempre con fecha de inicio y fecha de fin.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'auto',
    },
    {
      target: '#vacation-tour-consequences',
      content: 'Elegí las fechas de tu período y este cuadro te muestra al instante el impacto en tu agenda. En este ejemplo de 20 días, la fila resaltada te dice que se bloquea 60 días antes de que empieces.',
      disableBeacon: true,
      disableOverlayClose: true,
      spotlightPadding: 6,
      placement: 'auto',
    },
    {
      target: '#vacations-tour-list',
      content: 'Acá vas a ver todos tus períodos cargados, con su estado: Próximo, En curso o Finalizado. Podés modificar un período mientras no haya finalizado.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'top',
    },
    {
      target: '#profile-vacations-section',
      content: 'Esta sección es tuya: te da visibilidad completa de tus días off para que organices tu descanso con tranquilidad. Informalo con anticipación y tu agenda se ajusta sola para que no te asignen pacientes nuevos mientras estás fuera.',
      disableBeacon: true,
      spotlightPadding: 6,
      placement: 'top',
    },
  ];

  const VacationsJoyrideTooltip: React.FC<JoyrideTooltipProps> = ({ index, step, backProps, primaryProps, tooltipProps }) => {
    const total = vacationsSteps.length;
    const current = (Number(index) || 0) + 1;
    const isLast = current >= total;
    return (
      <div
        {...tooltipProps}
        style={{ background: '#fff', borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', maxWidth: 420, padding: '1rem 1rem 0.75rem 1rem', color: '#111827' }}
      >
        <div style={{ marginBottom: '0.75rem' }}>{step?.content}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', paddingTop: '0.25rem' }}>
          {index > 0 && (
            <button {...backProps} className="btn-outline" style={{ height: 32 }}>
              Atrás
            </button>
          )}
          <button
            {...primaryProps}
            className="btn-primary"
            style={{ height: 32, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px', whiteSpace: 'nowrap', lineHeight: '32px' }}
          >
            {isLast ? 'Entendido' : `Continuar (${current} de ${total})`}
          </button>
        </div>
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const VacationsTooltipAny = VacationsJoyrideTooltip as any;

  const onVacationsTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & { action?: string };
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunVacationsTour(false);
      try { localStorage.setItem('psi-vacations-tour-done', '1'); } catch { /* noop */ }
      markTourCompleteOnServer('psi-vacations-tour-done');
      checkAndMarkOnboardingComplete();
      return;
    }
    // El paso 1 (índice 1) apunta al modal "Informar vacaciones" — ProfileVacationsTab lo
    // abre recién cuando `vacationsStepIndex` pasa a valer 1 (ver tourShowDemoModal), un
    // ciclo de render después. Si Joyride llega a chequear el target antes de que el modal
    // termine de montarse, remontamos (tourKey++) para que reintente una vez que ya esté.
    if (type === EVENTS.TARGET_NOT_FOUND && index === 1) {
      window.setTimeout(() => setVacationsTourKey((prev) => prev + 1), 100);
      return;
    }
    if (type === 'step:after') {
      if (action === 'next') setVacationsStepIndex((index ?? 0) + 1);
      if (action === 'prev') setVacationsStepIndex(Math.max(0, (index ?? 0) - 1));
    }
  };

  // Auto-arranca la primera vez que se entra a "Vacaciones y Días off", una vez que el
  // tour principal de Perfil ya terminó.
  useEffect(() => {
    if (isPwaApp || activeTab !== 'vacations' || runProfileTour) return;
    try {
      if (localStorage.getItem('psi-profile-tour-done') !== '1') return;
      if (localStorage.getItem('psi-vacations-tour-done') === '1') return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => {
      setVacationsStepIndex(0);
      setRunVacationsTour(true);
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, runProfileTour]);

  useEffect(() => {
    if (user?.name) {
      const names = parseUserName();
      setProfileData(prev => ({
        ...prev,
        name: names.firstName,
        lastName: names.lastName,
      }));
    }
  }, [user?.name, parseUserName]);

  useEffect(() => {
    if (services && services.length > 0) {
      const mappedServices = services.map(s => ({ name: s.service.name, active: true }));
      setProfessionalData(prev => ({
        ...prev,
        services: mappedServices,
      }));
    }
  }, [services]);

  // Fetch psychologist profile from backend
  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      try {
        setLoadingProfile(true);
        const resp = await apiCoreAdapter.psychologistProfile();
        if (cancelled) return;
        // Procesos activos desde el mismo endpoint /profile
        if (typeof resp?.current_process_count === 'number') {
          setCurrentAssignedProcesses(resp.current_process_count);
        }
        // Límite interno (process_limit): sincronizar el contexto con el valor fresco de
        // /profile. Antes se descartaba y se seguía usando `psychologist.processLimit`
        // (cacheado desde el listado de psicólogas), que podía quedar desactualizado si el
        // límite interno cambiaba en el backend — y ese valor stale es el mismo que se
        // reenviaba al guardar "Tu preferencia" (ver `internalLimit` más abajo), pudiendo
        // pisar sin querer un límite interno actualizado.
        const currentPsychologist = psychologistRef.current;
        if (typeof resp?.process_limit === 'number' && currentPsychologist && resp.process_limit !== currentPsychologist.processLimit) {
          setPsychologist({ ...currentPsychologist, processLimit: resp.process_limit });
        }
        const apiName = resp?.name || initialNames.firstName;
        const apiSurname = resp?.surname || initialNames.lastName;
        const isoBirth = resp?.birthdate;
        const phone = resp?.phone || '';
        setProfileData(prev => ({
          ...prev,
          name: apiName,
          lastName: apiSurname,
          birthDate: toISODateOnly(isoBirth) || prev.birthDate,
          phone: phone || prev.phone,
          profileImage: (resp as unknown as { profile_photo?: string })?.profile_photo || googlePicture || prev.profileImage || null,
        }));
        if (resp?.timezone !== undefined) setProfileTimezone(resp.timezone ?? null);
        // Fecha de alta real
        if (resp?.created_at) {
          try {
            const d = new Date(resp.created_at);
            if (!isNaN(d.getTime())) {
              const formatted = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
              setProfessionalData(prev => ({ ...prev, joinDate: formatted }));
            }
          } catch { /* keep mock */ }
        }

        // Build base services (always visible)
        const baseServices = [
          'Individual',
          'Orientación vocacional',
          'Orientación a padres',
          'Familia',
          'Pareja',
          'Supervisión',
          'Material Digital',
          'Talleres/charlas',
        ];
        const apiServices = Array.isArray(resp?.services) ? resp.services : [];
        const normalizeLabel = (s: string) => {
          let t = (s || '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
          // Unificar variantes frecuentes
          t = t.replace(/\blgbtiq\+?\b/g, 'lgbtq');
          t = t.replace(/\b1era\b|\b1ra\b/g, 'primera');
          t = t.replace(/\bjunguiana\b/g, 'junguiano'); // backend "Junguiana" vs UI "Junguiano"
          t = t.replace(/-/g, ' ');
          // Compactar separadores
          t = t.replace(/[^a-z0-9/+]+/g, ' ').replace(/\s+/g, ' ').trim();
          return t;
        };
        const serviceMatch = (srv: string): string | null => {
          const s = normalizeLabel(String(srv));
          if (s.includes('individual')) return 'Individual';
          if (s.includes('vocacion')) return 'Orientación vocacional';
          if (s.includes('padre')) return 'Orientación a padres';
          if (s.includes('famil')) return 'Familia';
          if (s.includes('pareja') || s.includes('couple')) return 'Pareja';
          if (s.includes('supervis')) return 'Supervisión';
          if (s.includes('taller') || s.includes('charla')) return 'Talleres/charlas';
          if (s.includes('downloadable') || s.includes('material')) return 'Material Digital';
          return null;
        };
        const activeSet = new Set<string>();
        apiServices.forEach(s => {
          const m = serviceMatch(String(s));
          if (m) activeSet.add(m);
        });
        const servicesMapped = baseServices.map(n => ({ name: n, active: activeSet.has(n) }));

        // Aplicar estado de certificación "rejected_escalated" desde localStorage.
        // Si la API ahora incluye el servicio como activo (fue re-subido y aprobado) → limpiar.
        const rejectedFromStorage: string[] = (() => {
          try { return JSON.parse(localStorage.getItem(CERT_REJECTED_KEY) || '[]') as string[]; } catch { return []; }
        })();
        const stillRejected = rejectedFromStorage.filter(name => !activeSet.has(name));
        if (stillRejected.length !== rejectedFromStorage.length) localStorage.setItem(CERT_REJECTED_KEY, JSON.stringify(stillRejected));
        const servicesMappedFinal: ServiceItem[] = servicesMapped.map(s => ({
          ...s,
          certStatus: stillRejected.includes(s.name) ? 'rejected_escalated' as const : undefined,
        }));

        // Nombres de topics/specializations activos — se usan solo como base del
        // diff al guardar (originalProfile más abajo). El catálogo y el estado
        // "active" de temáticas/enfoques que ve la psicóloga vienen aparte, del
        // catálogo dinámico (ver fetchPreferencesCatalog): ese sí matchea por id
        // contra clinical_preferences, sin depender de normalizar nombres a mano.
        // Las keys son clinical_preferences.type (singular: "topic",
        // "specialization", ...) — no "topics"/"specializations" (ver
        // psiplatform-be/psychologist-profile.service.ts mapProfile).
        const topics: string[] = Array.isArray(resp?.preferences?.topic) ? resp?.preferences?.topic as string[] : [];
        const specs: string[] = Array.isArray(resp?.preferences?.specialization) ? resp?.preferences?.specialization as string[] : [];

        setProfessionalData(prev => ({
          ...prev,
          services: servicesMappedFinal,
        }));
        // Guardar snapshot original para calcular delta (API names para topics/specs)
        setOriginalProfile({
          name: apiName,
          surname: apiSurname,
          birthdate: isoBirth || undefined,
          phone,
          services: servicesMappedFinal.filter(s => s.active).map(s => s.name),
          topics: topics,
          specializations: specs,
          // Cualquier otro type que venga en preferences además de topic/specialization
          // (sex, personality, ...) — snapshot inicial para el diff de otras categorías.
          otherPreferences: Object.fromEntries(
            Object.entries(resp?.preferences ?? {}).filter(([type]) => type !== 'topic' && type !== 'specialization'),
          ) as Record<string, string[]>,
        });
      } catch (_e) {
        // keep mock/fallbacks
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
          setProfileLoaded(true);
        }
      }
    }
    loadProfile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catálogo dinámico de "Sobre tu atención" (temáticas/enfoques) — reemplaza
  // el array hardcodeado que tenía este componente. Viene de psiplatform-be,
  // que a su vez lo arma con la config de visibilidad propia de psi-platform
  // en be-make (misma fuente que ya usa el backoffice de psicólogas). Falla
  // en silencio a catálogo vacío: el resto del perfil sigue funcionando.
  useEffect(() => {
    let cancelled = false;
    async function loadPreferencesCatalog() {
      try {
        setPreferencesCatalogLoading(true);
        const catalog = await apiCoreAdapter.psychologistPreferencesCatalog();
        if (cancelled) return;
        // Temáticas/Enfoques tienen su propia sección con reglas de negocio
        // puntuales (ver toggleTag) — el resto del catálogo (writable o no)
        // se muestra aparte, en otherPreferenceGroups.
        const DEDICATED_SECTION_FILTER_KEYS = ['topic_preference', 'specialization'];
        // Orden alfabético (no el que trae el catálogo, que es el que
        // definió el admin en quieroserpaciente) — pedido explícito: las
        // opciones dentro de cada sección de "Sobre tu atención" van de A a Z.
        const byNameAsc = (a: { name: string }, b: { name: string }) =>
          a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
        const toItems = (filterKey: string) =>
          (catalog
            .find(g => g.filterKey === filterKey)
            ?.options.map(o => ({ name: o.name, active: o.active })) ?? [])
            .sort(byNameAsc);
        setProfessionalData(prev => ({
          ...prev,
          themes: toItems('topic_preference'),
          approaches: toItems('specialization'),
        }));
        setDedicatedSectionLabels(prev => ({
          themes: catalog.find(g => g.filterKey === 'topic_preference')?.label ?? prev.themes,
          approaches: catalog.find(g => g.filterKey === 'specialization')?.label ?? prev.approaches,
        }));
        setOtherPreferenceGroups(
          catalog
            .filter(g => !DEDICATED_SECTION_FILTER_KEYS.includes(g.filterKey))
            .map(g => ({
              filterKey: g.filterKey,
              label: g.label,
              type: g.type,
              writable: g.writable,
              items: g.options.map(o => ({ name: o.name, active: o.active })).sort(byNameAsc),
            })),
        );
      } catch (_e) {
        // Catálogo vacío — la sección queda sin opciones hasta que se resuelva.
      } finally {
        if (!cancelled) setPreferencesCatalogLoading(false);
      }
    }
    loadPreferencesCatalog();
    return () => { cancelled = true; };
  }, []);

  // Estilo terapéutico — determina si la tab existe (questions.length > 0) y su
  // contenido. Falla en silencio a "oculto" (mismo criterio que el catálogo de arriba):
  // no elegible, sin preguntas activas, o quieroserpaciente caído son indistinguibles
  // para este componente, y las tres ocultan la tab por completo.
  useEffect(() => {
    let cancelled = false;
    async function loadTherapeuticStyle() {
      try {
        setTherapeuticStyleLoading(true);
        const data = await getTherapeuticStyleState();
        if (!cancelled) setTherapeuticStyleState(data);
      } catch {
        if (!cancelled) setTherapeuticStyleState(null);
      } finally {
        if (!cancelled) setTherapeuticStyleLoading(false);
      }
    }
    loadTherapeuticStyle();
    return () => { cancelled = true; };
  }, []);

  const therapeuticStyleAvailable =
    !therapeuticStyleLoading && !!therapeuticStyleState && therapeuticStyleState.questions.length > 0;

  // Si la tab de estilo terapéutico deja de estar disponible mientras está activa
  // (rollout que te sacó entre requests, o un deep-link viejo a un slug que ya no
  // aplica), volvemos a "general" en vez de dejar la tab abierta y vacía.
  useEffect(() => {
    if (activeTab === 'therapeuticStyle' && !therapeuticStyleLoading && !therapeuticStyleAvailable) {
      setActiveTab('general');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, therapeuticStyleLoading, therapeuticStyleAvailable]);

  const handleTherapeuticStyleAnswersSaved = useCallback((answers: Record<string, string>) => {
    setTherapeuticStyleState((prev) => (prev ? { ...prev, answers } : prev));
  }, []);

  // Fetch active vacation to show block banner
  const fetchActiveVacationBanner = React.useCallback(async () => {
    const psiId = psychologistId || psychologist?.id;
    if (!psiId) return;
    try {
      type VacItem = { from: string; to?: string; duration?: number; psychologist?: { email?: string; id?: string }; blockade?: { from: string; to?: string } };
      const res = await apiCoreAdapter.psychologistVacations({ psychologist: psiId, skip: 0, take: 100 }) as { results?: VacItem[] } | VacItem[];
      const raw: VacItem[] = Array.isArray(res)
        ? res
        : (res as { results?: VacItem[] }).results ?? [];

      const now = new Date();
      // Buscar cualquier registro cuyo `blockade` esté activo ahora
      const active = raw.find(v => {
        const b = v.blockade;
        if (b) {
          const bStart = new Date(b.from);
          const bEnd = b.to ? new Date(b.to) : null;
          if (now >= bStart && (!bEnd || now <= bEnd)) return true;
        }
        // Fallback: chequear fechas de vacación directamente
        const start = new Date(v.from);
        const end = v.to ? new Date(v.to) : v.duration ? new Date(start.getTime() + v.duration * 86400000) : null;
        return now >= start && (!end || now <= end);
      });

      if (!active) {
        setActiveVacationEnd(false);
        setActiveVacationDays(null);
      } else {
        // Usar fecha fin del blockade si existe, sino del to/duration de la vacación
        const endDate = active.blockade?.to || active.to ||
          (active.duration ? new Date(new Date(active.from).getTime() + active.duration * 86400000).toISOString() : null);
        setActiveVacationEnd(endDate || null);
        // Calcular duración en días de la vacación para resaltar la fila correcta en el modal
        const days = active.duration
          ? active.duration
          : active.to
            ? Math.round((new Date(active.to).getTime() - new Date(active.from).getTime()) / 86400000)
            : null;
        setActiveVacationDays(days);
      }
    } catch {
      setActiveVacationEnd(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologist?.id, psychologistId]);

  useEffect(() => {
    fetchActiveVacationBanner();
  }, [fetchActiveVacationBanner]);

  // process_limit_preferred — independiente del loadProfile para evitar problemas de closure/timing
  useEffect(() => {
    const psiId = psychologistId || psychologist?.id;
    if (!psiId) return;
    let cancelled = false;
    (async () => {
      try {
        const prefResp = await apiCoreAdapter.psychologistPreferences(psiId);
        type PrefItem = { id: string; psychologist?: { id?: string; process_limit_preferred?: number | null } };
        const list: PrefItem[] = Array.isArray(prefResp) ? prefResp : (prefResp as { results: PrefItem[] }).results ?? [];
        // Sin fallback a list[0]: si el filtro por psychologist_id no matcheó ningún item,
        // preferimos no mostrar nada antes que mostrar por error la preferencia de otra psico.
        const found = list.find(p => p.psychologist?.id === psiId);
        if (!cancelled) {
          const serverValue = found?.psychologist?.process_limit_preferred ?? null;
          setProcessLimitPrefered(serverValue);
          // Sincronizar el draft con el server al cargar
          setProcessLimitPreferredDraft(serverValue);
        }
      } catch { /* silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [psychologistId, psychologist?.id]);


  // Bloqueos del calendario (tope de pacientes, sin horarios, situaciones personales) — nombres
  // y copy centralizados en utils/calendarBlockadeInfo.ts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCoreAdapter.psychologistBlockades();
        const now = new Date();
        const found = (res.results as Array<Record<string, unknown>>).find(b => {
          if (!b.active) return false;
          const reason = b.reason as { name: string } | null;
          if (!reason || !CALENDAR_BLOCKADE_REASONS.includes(reason.name as CalendarBlockadeReason)) return false;
          const from = new Date(b.from as string);
          const to = b.to ? new Date(b.to as string) : null;
          return now >= from && (!to || now <= to);
        });
        if (!cancelled) {
          if (found) {
            const reason = (found.reason as { name: string }).name;
            const to = (found.to as string | null) ?? null;
            setActiveBlockade({ reason, to });
          } else {
            setActiveBlockade(false);
          }
        }
      } catch {
        if (!cancelled) setActiveBlockade(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Altas/bajas pendientes de guardar por chip, para el indicador visual en "Sobre tu atención".
  // Se deriva en vivo de professionalData vs originalProfile — se vacía solo al revertir el toggle
  // (vuelve a coincidir con lo original) o al guardar (originalProfile se actualiza con lo guardado).
  const pendingAttentionChanges = useMemo(() => {
    const diffNormalized = (currentNames: string[], originalNames: string[]) => {
      const currentNorm = new Set(currentNames.map(normalizeForCompare));
      const originalNorm = new Set(originalNames.map(normalizeForCompare));
      return {
        added: new Set(Array.from(currentNorm).filter(n => !originalNorm.has(n))),
        removed: new Set(Array.from(originalNorm).filter(n => !currentNorm.has(n))),
      };
    };
    return {
      services: diffNormalized(
        professionalData.services.filter(s => s.active).map(s => s.name),
        originalProfile.services || [],
      ),
      themes: diffNormalized(
        professionalData.themes.filter(t => t.active).map(t => t.name),
        originalProfile.topics || [],
      ),
      approaches: diffNormalized(
        professionalData.approaches.filter(a => a.active).map(a => a.name),
        originalProfile.specializations || [],
      ),
    };
  }, [professionalData.services, professionalData.themes, professionalData.approaches, originalProfile.services, originalProfile.topics, originalProfile.specializations]);

  // Espejo de pendingAttentionChanges, pero por filterKey — para los grupos
  // dinámicos writable de otherPreferenceGroups.
  const pendingOtherChanges = useMemo(() => {
    const diffNormalized = (currentNames: string[], originalNames: string[]) => {
      const currentNorm = new Set(currentNames.map(normalizeForCompare));
      const originalNorm = new Set(originalNames.map(normalizeForCompare));
      return {
        added: new Set(Array.from(currentNorm).filter(n => !originalNorm.has(n))),
        removed: new Set(Array.from(originalNorm).filter(n => !currentNorm.has(n))),
      };
    };
    const result: Record<string, { added: Set<string>; removed: Set<string> }> = {};
    for (const group of otherPreferenceGroups) {
      if (!group.writable) continue;
      // Ojo: originalProfile.otherPreferences está agrupado por `type`
      // (clinical_preferences.type, ej. "age group") — así viene de
      // GET /profile — no por `filterKey` de be-make (ej. "patient_age"),
      // que puede ser otro string distinto. La key del resultado sí es
      // filterKey (así la consume ProfileAttentionTab).
      result[group.filterKey] = diffNormalized(
        group.items.filter(i => i.active).map(i => i.name),
        originalProfile.otherPreferences[group.type] || [],
      );
    }
    return result;
  }, [otherPreferenceGroups, originalProfile.otherPreferences]);

  if (loadingProfile) {
    return (
      <div className="flex flex-col items-center p-6 bg-[#F6F4FE] min-h-screen">
        <div className="w-full max-w-[900px]">
          {/* Avatar + greeting card */}
          <div className="bg-white rounded-2xl p-6 mb-4 flex flex-col items-center gap-3 shadow-sm">
            <div className="w-16 h-16 skeleton-shimmer rounded-full" />
            <div className="h-5 w-44 skeleton-shimmer rounded" />
          </div>
          {/* Tab bar */}
          <div className="flex gap-2 mb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-9 flex-1 skeleton-shimmer rounded-full" />
            ))}
          </div>
          {/* Form card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="mb-5 last:mb-0">
                <div className="h-3 w-24 skeleton-shimmer rounded mb-2" />
                <div className="h-10 skeleton-shimmer rounded-lg w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Convierte un valor crudo devuelto por el backend (ej: "Embarazo - puerperio y 1era infancia")
  // a su etiqueta legible de UI (ej: "Embarazo, puerperio y primera infancia") buscando por
  // coincidencia normalizada en la lista de referencia. Si no encuentra match, devuelve el crudo.
  // Necesario porque topics/specializations se guardan en originalProfile con el formato exacto
  // del backend (a diferencia de services, que ya se normaliza a etiqueta de UI al cargar).
  const apiLabelToUi = (raw: string, uiList: Array<{ name: string }>): string => {
    const normalizedRaw = normalizeForCompare(raw);
    const match = uiList.find(item => normalizeForCompare(item.name) === normalizedRaw);
    return match ? match.name : raw;
  };

  const handleOVConfirm = () => {
    if (pendingOVIndex === null) return;
    const idx = pendingOVIndex;
    setProfessionalData(prev => {
      const next = { ...prev, services: prev.services.map(item => ({ ...item })) };
      next.services[idx].active = true;
      const idxInd = next.services.findIndex(s => s.name === 'Individual');
      if (idxInd >= 0) next.services[idxInd].active = true;
      return next;
    });
    setHasChanges(true);
    setShowOVModal(false);
    setPendingOVIndex(null);
  };

  const handleOVCancel = () => {
    setShowOVModal(false);
    setPendingOVIndex(null);
  };

  const toggleTag = (category: 'services' | 'themes' | 'approaches', index: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Evita que se cierre la card
    // Interceptar activación de Orientación Vocacional
    if (category === 'services') {
      const svc = professionalData.services[index] as ServiceItem;
      // Cert escalado (rejected_escalated): limpiar estado y dejar que el usuario active el servicio de nuevo
      if (svc?.certStatus === 'rejected_escalated') {
        const rejected: string[] = (() => { try { return JSON.parse(localStorage.getItem(CERT_REJECTED_KEY) || '[]') as string[]; } catch { return []; } })();
        localStorage.setItem(CERT_REJECTED_KEY, JSON.stringify(rejected.filter(s => s !== svc.name)));
        setProfessionalData(prev => ({
          ...prev,
          services: prev.services.map(s =>
            s.name === svc.name ? ({ ...s, certStatus: undefined, active: false }) as ServiceItem : s
          ),
        }));
        certValidation.reset(svc.name);
        toastUtils.info(`Podés activar "${svc.name}" y subir nueva documentación.`);
        return;
      }
      // Bloquear interacción mientras la validación IA está en curso o pidiendo más documentación
      const liveStatus = svc && certValidation.states[svc.name]?.status;
      if (liveStatus === 'validating' || liveStatus === 'needs_additional_document') {
        toastUtils.info(`Estamos procesando la documentación de "${svc.name}". Continuá desde "Guardar Cambios".`);
        return;
      }
      if (svc && !svc.active && normalizeForCompare(svc.name) === 'orientacion vocacional') {
        setPendingOVIndex(index);
        setShowOVModal(true);
        return;
      }
    }
    setProfessionalData(prev => {
      // Copy current
      const next = { ...prev, [category]: prev[category].map((item) => ({ ...item })) };
      const list = next[category] as Array<{ name: string; active: boolean }>;
      const current = list[index];
      if (!current) return prev;
      // Rules
      if (category === 'services') {
        // No permitir desactivar Individual
        if (current.name === 'Individual' && current.active) {
          return prev;
        }
        current.active = !current.active;
        // Asegurar que Individual esté activo siempre
        const idxInd = list.findIndex(s => s.name === 'Individual');
        if (idxInd >= 0) list[idxInd].active = true;
      } else if (category === 'approaches') {
        const activeCount = list.filter(a => a.active).length;
        if (current.active && activeCount <= 1) {
          // Evitar dejar 0 enfoques activos
          return prev;
        }
        current.active = !current.active;
      } else {
        // themes - impedir desactivar "Desarrollo personal"
        const isPersonalDevelopment = normalizeForCompare(current.name) === 'desarrollo personal';
        if (isPersonalDevelopment && current.active) {
          return prev;
        }
        current.active = !current.active;
      }
      return next;
    });
    setHasChanges(true);
  };

  // Toggle simple, sin las reglas de negocio de toggleTag (mínimo activo,
  // ítem obligatorio, etc.) — no aplican a un filtro genérico que no sabemos
  // de antemano cómo se usa. Solo actúa si el grupo es writable; el caller
  // (ProfileAttentionTab) ya no ofrece el click si no lo es.
  const toggleOtherPreference = (filterKey: string, index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setOtherPreferenceGroups(prev => prev.map(group => {
      if (group.filterKey !== filterKey || !group.writable) return group;
      const items = group.items.map((item, i) => i === index ? { ...item, active: !item.active } : item);
      return { ...group, items };
    }));
    setHasChanges(true);
  };

  const patientLimitDirty = processLimitPreferredDraft !== null
    && processLimitPreferredDraft !== processLimitPrefered;
  // `hasChanges` es solo "se tocó algo alguna vez" (queda en true aunque se
  // togglee de vuelta al estado original) — canSave usa el diff real
  // (pendingAttentionChanges/pendingOtherChanges, ya comparados contra
  // originalProfile) para que el botón se desactive de nuevo si no queda
  // ningún cambio neto pendiente.
  const hasPendingDiff =
    pendingAttentionChanges.services.added.size > 0 || pendingAttentionChanges.services.removed.size > 0 ||
    pendingAttentionChanges.themes.added.size > 0 || pendingAttentionChanges.themes.removed.size > 0 ||
    pendingAttentionChanges.approaches.added.size > 0 || pendingAttentionChanges.approaches.removed.size > 0 ||
    Object.values(pendingOtherChanges).some(d => d.added.size > 0 || d.removed.size > 0);
  const canSave = hasPendingDiff || patientLimitDirty;

  // Si el único cambio pendiente es el tope de pacientes, el guardado se saltea el
  // Paso 2 (Declaración de responsabilidad), que aplica solo a servicios/temáticas/enfoques.
  const onlyPatientLimitChange =
    changesDiff.services.added.length === 0 && changesDiff.services.removed.length === 0 &&
    changesDiff.themes.added.length === 0 && changesDiff.themes.removed.length === 0 &&
    changesDiff.approaches.added.length === 0 && changesDiff.approaches.removed.length === 0 &&
    Object.values(changesDiff.other).every(d => d.added.length === 0 && d.removed.length === 0) &&
    pendingCertServices.length === 0;

  const handlePatientLimitChange = (next: number) => {
    setProcessLimitPreferredDraft(next);
  };

  /**
   * Una BAJA ya se aplicó de verdad en el backend (ver PatientLimitField). A diferencia de
   * handlePatientLimitChange, acá sincronizamos también el valor "guardado" — no solo el
   * draft — para que no quede marcado como cambio pendiente esperando un "Guardar Cambios"
   * que ya no hace falta para este campo.
   */
  const handlePatientLimitApplied = (next: number) => {
    setProcessLimitPreferredDraft(next);
    setProcessLimitPrefered(next);
  };

  const requiresCertNewlyAdded = (): boolean => getNewlyAddedCertServices().length > 0;
  const getNewlyAddedCertServices = (): string[] => {
    const currentServicesEs = professionalData.services.filter(s => s.active).map(s => s.name);
    const originallyHad = new Set((originalProfile.services || []).map(s => s));
    const nowHas = new Set(currentServicesEs);
    // Excluye servicios que ya tienen una solicitud pendiente (otra pestaña/sesión) —
    // evita pedirle a la psicóloga que reenvíe documentación ya enviada.
    const alreadyPending = new Set(readPendingCertServices());
    return ['Familia', 'Pareja', 'Orientación vocacional'].filter(s => nowHas.has(s) && !originallyHad.has(s) && !alreadyPending.has(s));
  };

  const handleSaveChanges = () => {
    if (!canSave) return;
    // Si se agregaron servicios que requieren certificado, preparar la carga en el popup
    if (requiresCertNewlyAdded()) {
      setCertRequestedThisFlow(true);
      setCertFilesMap({});
      setPendingCertServices(getNewlyAddedCertServices());
    } else {
      setPendingCertServices([]);
    }
    // Detectar si hubo cambios en Servicios/Temáticas/Enfoques para el mensaje del modal
    const normalizeSet = (arr: string[]) => new Set(arr.map(normalizeForCompare));
    const setsDiffer = (a: Set<string>, b: Set<string>): boolean => {
      if (a.size !== b.size) return true;
      let different = false;
      a.forEach((val) => { if (!b.has(val)) different = true; });
      if (different) return true;
      b.forEach((val) => { if (!a.has(val)) different = true; });
      return different;
    };
    const currentServices = professionalData.services.filter(s => s.active).map(s => s.name);
    const currentTopics = mapTopicsForApi(professionalData.themes.filter(t => t.active).map(t => t.name));
    const currentApproaches = mapSpecializationsForApi(professionalData.approaches.filter(a => a.active).map(a => a.name));
    const hasServiceChanges = setsDiffer(normalizeSet(currentServices), normalizeSet(originalProfile.services || []));
    const hasTopicsChanges = setsDiffer(normalizeSet(currentTopics), normalizeSet(originalProfile.topics || []));
    const hasApproachChanges = setsDiffer(normalizeSet(currentApproaches), normalizeSet(originalProfile.specializations || []));
    const hasOtherChanges = otherPreferenceGroups.some(g => g.writable
      && setsDiffer(
        normalizeSet(g.items.filter(i => i.active).map(i => i.name)),
        normalizeSet(originalProfile.otherPreferences[g.type] || []),
      ));
    const hasAttentionChanges = hasServiceChanges || hasTopicsChanges || hasApproachChanges || hasOtherChanges;
    if (hasAttentionChanges || patientLimitDirty) {
      // Calcular diff visual
      const origServicesSet = new Set(originalProfile.services || []);
      const currServicesSet = new Set(currentServices);
      const origTopicsNorm = new Set((originalProfile.topics || []).map(t => normalizeForCompare(t)));
      const currThemeNames = professionalData.themes.filter(t => t.active).map(t => t.name);
      const origSpecsNorm = new Set((originalProfile.specializations || []).map(s => normalizeForCompare(s)));
      const currApproachNames = professionalData.approaches.filter(a => a.active).map(a => a.name);
      setChangesDiff({
        services: {
          added: currentServices.filter(s => !origServicesSet.has(s)),
          removed: (originalProfile.services || []).filter(s => !currServicesSet.has(s)),
        },
        themes: {
          added: currThemeNames.filter(n => !origTopicsNorm.has(normalizeForCompare(n))),
          removed: (originalProfile.topics || [])
            .filter(t => !currThemeNames.some(n => normalizeForCompare(n) === normalizeForCompare(t)))
            .map(t => apiLabelToUi(t, professionalData.themes)),
        },
        approaches: {
          added: currApproachNames.filter(n => !origSpecsNorm.has(normalizeForCompare(n))),
          removed: (originalProfile.specializations || [])
            .filter(s => !currApproachNames.some(n => normalizeForCompare(n) === normalizeForCompare(s)))
            .map(s => apiLabelToUi(s, professionalData.approaches)),
        },
        other: Object.fromEntries(
          otherPreferenceGroups.filter(g => g.writable).map(g => {
            const origNorm = new Set((originalProfile.otherPreferences[g.type] || []).map(n => normalizeForCompare(n)));
            const currNames = g.items.filter(i => i.active).map(i => i.name);
            return [g.filterKey, {
              label: g.label,
              added: currNames.filter(n => !origNorm.has(normalizeForCompare(n))),
              removed: (originalProfile.otherPreferences[g.type] || [])
                .filter(n => !currNames.some(c => normalizeForCompare(c) === normalizeForCompare(n))),
            }];
          }),
        ),
        patientLimit: patientLimitDirty
          ? { from: processLimitPrefered, to: processLimitPreferredDraft }
          : { from: null, to: null },
      });
      setSavePopupStep(1);
      setShowSavePopup(true);
    } else {
      // Solo cambios generales (nombre, fecha, teléfono): guardar directo sin popup
      confirmSave();
    }
  };

  // Enfoques (specializations) -> valores exactos del backend
  const specializationMapNormalizedToApi: Record<string, string> = {
    'tcc': 'TCC',
    'junguiano / transpersonal': 'Junguiana / Transpersonal',
    'emdr': 'EMDR',
    'ingles': 'Inglés',
    'sistémica': 'Sistemica',
    'sistemica': 'Sistemica',
    'humanista': 'Humanista',
    'deporte': 'Deporte',
    'gestalt': 'Gestalt',
    'integral': 'Integral',
    'psicoanalisis': 'Psicoanálisis',
    'dbt': 'DBT',
    'genero': 'Género',
    'psicologia positiva': 'Psicologia positiva',
    'transpersonal': 'Transpersonal',
    'sexologia': 'Sexologia',
    'act': 'ACT',
    'mindfulness': 'Mindfulness',
  };
  const mapSpecializationsForApi = (items: string[]): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const it of items) {
      const api = specializationMapNormalizedToApi[normalizeForCompare(it)] || it;
      if (!seen.has(api)) { seen.add(api); out.push(api); }
    }
    return out;
  };
  // Temáticas (topics) -> valores exactos del backend
  const topicsMapNormalizedToApi: Record<string, string> = {
    'desarrollo personal': 'Desarrollo personal',
    'adicciones': 'Adicciones',
    'bipolaridad / tlp': 'Bipolaridad / TLP',
    'cuidados paliativos': 'Cuidados paliativos',
    'dependencia emocional': 'Dependencia Emocional',
    'depresion': 'Depresión',
    'duelos': 'Duelos',
    'embarazo puerperio y primera infancia': 'Embarazo - puerperio y 1era infancia',
    'embarazo , puerperio y primera infancia': 'Embarazo - puerperio y 1era infancia',
    'narcisismo y violencia': 'Narcisismo y violencia',
    'obesidad y tca': 'Obesidad y TCA',
    'relaciones personales': 'Relaciones personales',
    'sexualidad / relaciones no monogamas': 'Sexualidad / Relaciones no monogamas',
    'tag / fobias': 'TAG / Fobias',
    'tdah': 'TDAH',
    'tea': 'TEA',
    'toc': 'TOC',
    'transexualidad / lgbtq+': 'Transexualidad / LGBTIQ+',
    'trauma / tept / abuso sexual': 'Trauma / TEPT / Abuso sexual',
  };
  const mapTopicsForApi = (items: string[]): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const it of items) {
      const api = topicsMapNormalizedToApi[normalizeForCompare(it)] || it;
      if (!seen.has(api)) { seen.add(api); out.push(api); }
    }
    return out;
  };
  const toIsoMidnight = (dateStr?: string) => {
    if (!dateStr) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return `${dateStr}T00:00:00.000Z`;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return undefined;
    return `${d.toISOString().slice(0, 10)}T00:00:00.000Z`;
  };

  const confirmSave = async () => {
    try {
      // Build current selections
      const currentServicesEs = professionalData.services.filter(s => s.active).map(s => s.name);
      // Garantizar 'Individual'
      if (!currentServicesEs.includes('Individual')) currentServicesEs.push('Individual');
      // No enviar servicios que requieren certificado (Familia/Pareja/Orientación vocacional)
      const requiresCertServices = ['Familia', 'Pareja', 'Orientación vocacional'];
      const filteredServicesEs = currentServicesEs.filter(s => !requiresCertServices.includes(s));
      const currentTopics = mapTopicsForApi(professionalData.themes.filter(t => t.active).map(t => t.name));
      const currentSpecs = mapSpecializationsForApi(professionalData.approaches.filter(a => a.active).map(a => a.name));

      // Para el mensaje (usar etiquetas legibles)
      const currentTopicsUI = professionalData.themes.filter(t => t.active).map(t => t.name);
      const currentSpecsUI = professionalData.approaches.filter(a => a.active).map(a => a.name);

      const toNormSet = (arr?: string[]) => new Set((arr || []).map(normalizeForCompare));

      // Compute deltas
      type UpdatePayload = Partial<{
        name: string;
        surname: string;
        birthdate: string;
        phone: string;
      }>;
      const payload: UpdatePayload = {};
      if (profileData.name && profileData.name !== originalProfile.name) payload.name = profileData.name;
      if (profileData.lastName && profileData.lastName !== originalProfile.surname) payload.surname = profileData.lastName;
      const isoBirth = toIsoMidnight(profileData.birthDate);
      if (isoBirth && isoBirth !== originalProfile.birthdate) payload.birthdate = isoBirth;
      if (profileData.phone && profileData.phone !== originalProfile.phone) payload.phone = profileData.phone;

      // Services delta
      const origServicesNorm = new Set(originalProfile.services || []);
      const currServicesNorm = new Set(filteredServicesEs);
      const servicesChanged = filteredServicesEs.length !== (originalProfile.services || []).length
        || filteredServicesEs.some(s => !origServicesNorm.has(s))
        || (originalProfile.services || []).some(s => !currServicesNorm.has(s));

      // Preferences delta
      const origTopicsSet = toNormSet(originalProfile.topics);
      const currTopicsSet = toNormSet(currentTopics);
      const topicsChanged = currTopicsSet.size !== origTopicsSet.size
        || Array.from(currTopicsSet).some(t => !origTopicsSet.has(t));

      const origSpecsSet = toNormSet(originalProfile.specializations);
      const currSpecsSet = toNormSet(currentSpecs);
      const specsChanged = currSpecsSet.size !== origSpecsSet.size
        || Array.from(currSpecsSet).some(s => !origSpecsSet.has(s));

      // Filtros dinámicos writable (además de Temáticas/Enfoques) — se mandan
      // por clinical_preferences.type (g.type), no por filterKey.
      const currentOtherByType: Record<string, string[]> = {};
      let otherChanged = false;
      for (const group of otherPreferenceGroups) {
        if (!group.writable) continue;
        const names = group.items.filter(i => i.active).map(i => i.name);
        currentOtherByType[group.type] = names;
        const origSet = toNormSet(originalProfile.otherPreferences[group.type]);
        const currSet = toNormSet(names);
        if (currSet.size !== origSet.size || Array.from(currSet).some(n => !origSet.has(n))) {
          otherChanged = true;
        }
      }

      // Determinar tipos de cambios según nueva lógica
      const attentionChanged = servicesChanged || topicsChanged || specsChanged || otherChanged;
      const generalChanged = Boolean(payload.name || payload.surname || payload.birthdate || payload.phone);

      // Ejecutar acciones según los cambios detectados
      setSavingProfile(true);

      // 1) Cambios de información general
      if (generalChanged) {
        await apiCoreAdapter.updatePsychologistProfile(payload);
        // Actualizar solo los campos generales en el snapshot
        setOriginalProfile(prev => ({
          ...prev,
          name: profileData.name,
          surname: profileData.lastName,
          birthdate: isoBirth || prev.birthdate,
          phone: profileData.phone,
        }));
      }

      // 2) Cambio del tope preferido de pacientes
      if (patientLimitDirty && processLimitPreferredDraft != null) {
        const psiId = psychologistId || psychologist?.id;
        let internalLimit = psychologist?.processLimit;
        // El límite interno puede no estar en memoria todavía (ver el efecto de arriba que
        // lo recarga "en silencio" si falta). Antes esto hacía que el guardado real se
        // salteara sin avisar — la psicóloga apretaba "Guardar Cambios" con toda la intención
        // de aplicar el cambio, y el flujo seguía como si hubiese salido bien (incluido el
        // mail de confirmación del paso 3c). Lo refrescamos acá mismo, en el momento del
        // guardado, en vez de depender del timing de ese efecto.
        if (typeof internalLimit !== 'number' && psiId) {
          try {
            const resp = await getPsychologists({ id: psiId, take: 1 });
            internalLimit = resp.data.find(p => String(p.id) === String(psiId))?.process_limit;
          } catch {
            // el chequeo de abajo se encarga de fallar explícito
          }
        }
        if (!psiId || typeof internalLimit !== 'number') {
          toastUtils.error('No pudimos guardar tu nueva preferencia de pacientes. Probá de nuevo en unos segundos.');
          setSavingProfile(false);
          return;
        }
        await apiCoreAdapter.updatePsychologistPatientLimit(psiId, {
          process_limit: internalLimit,
          process_limit_preferred: processLimitPreferredDraft,
        });
        // Métricas de Actividad: el otro camino al mismo cambio (PatientLimitField cuenta el suyo al aplicarlo; acá solo
        // llega un borrador distinto del valor guardado, así que no hay doble conteo).
        activityTracker.trackAction('patient_limit_changed');
        setProcessLimitPrefered(processLimitPreferredDraft);
      }

      // Variables de deltas (accesibles para certs y para email combinado)
      // Usar normalizeForCompare para evitar falsos positivos por diferencias de casing/acentos
      // entre los valores guardados por la API (e.g. "Dependencia Emocional") y los labels
      // del UI (e.g. "Dependencia emocional"). Sin normalización, un ítem sin cambios aparece
      // como agregado Y quitado al mismo tiempo en el email.
      const addedServices = professionalData.services
        .filter(s => s.active && !originalProfile.services.some(o => normalizeForCompare(o) === normalizeForCompare(s.name)))
        .map(s => s.name);
      const removedServices = (originalProfile.services || [])
        .filter(s => !professionalData.services.some(ps => ps.active && normalizeForCompare(ps.name) === normalizeForCompare(s)));
      const addedTopics = currentTopicsUI.filter(
        t => !originalProfile.topics.some(o => normalizeForCompare(o) === normalizeForCompare(t))
      );
      const removedTopics = (originalProfile.topics || [])
        .filter(t => !currentTopicsUI.some(c => normalizeForCompare(c) === normalizeForCompare(t)));
      const addedSpecs = currentSpecsUI.filter(
        a => !originalProfile.specializations.some(o => normalizeForCompare(o) === normalizeForCompare(a))
      );
      const removedSpecs = (originalProfile.specializations || [])
        .filter(a => !currentSpecsUI.some(c => normalizeForCompare(c) === normalizeForCompare(a)));
      // Mismo criterio que arriba, pero genérico por cada grupo dinámico writable
      // — un {category, name} por ítem, category = el label del grupo (ej. "Sexo").
      const otherAddedItems: Array<{ category: string; name: string }> = [];
      const otherRemovedItems: Array<{ category: string; name: string }> = [];
      for (const group of otherPreferenceGroups) {
        if (!group.writable) continue;
        const currNames = group.items.filter(i => i.active).map(i => i.name);
        const origNames = originalProfile.otherPreferences[group.type] || [];
        currNames
          .filter(n => !origNames.some(o => normalizeForCompare(o) === normalizeForCompare(n)))
          .forEach(name => otherAddedItems.push({ category: group.label, name }));
        origNames
          .filter(n => !currNames.some(c => normalizeForCompare(c) === normalizeForCompare(n)))
          .forEach(name => otherRemovedItems.push({ category: group.label, name }));
      }
      const firstName = profileData.name || user?.name || '';

      // 3a) Activar (o escalar) los servicios con certificado ya validado por IA.
      // La validación real corrió en el paso 2 (ver ValidationResultPanel) — acá solo
      // ejecutamos el resultado terminal (approved/rejected_escalated); needs_additional_document
      // no puede llegar hasta aquí porque bloquea el avance del paso 2.
      type CertOutcome = { service: string; status: CertificationValidationStatus; filename?: string };
      const certOutcomes: CertOutcome[] = [];
      if (pendingCertServices.length > 0) {
        const certToastId = toastUtils.loading('Activando servicios validados…');
        try {
          for (const service of pendingCertServices) {
            const validation = certValidation.states[service]?.result;
            if (!validation) continue;
            certOutcomes.push({ service, status: validation.status, filename: certFilesMap[service]?.name });
            if (validation.status === 'approved') {
              const clinicalServiceId = getClinicalServiceId(service);
              if (clinicalServiceId) {
                await http.post(`/v1/psychologists/${encodeURIComponent(psychologistId ?? '')}/services`, {
                  service: clinicalServiceId,
                });
              }
              // Guardar el certificado que efectivamente activó el servicio en el
              // mismo storage/lista que "Mi documentación" (mismo mecanismo que
              // handleDocUpload: category 'documents' -> folder 'documentacion').
              // Solo el certificado del outcome aprobado — nada de lo rechazado en
              // el camino ni de los documentos adicionales de intentos que no
              // terminaron en approved.
              const approvedFile = certFilesMap[service];
              if (approvedFile) {
                try {
                  const signed = await apiCoreAdapter.getSignedUrlForUpload({
                    filename: approvedFile.name,
                    contentType: approvedFile.type || 'application/octet-stream',
                    size: approvedFile.size,
                    category: 'documents',
                  });
                  await fetch(signed.url, { method: 'PUT', headers: signed.headers || {}, body: approvedFile });
                } catch {
                  // No bloquea la activación del servicio (ya ocurrió arriba) si falla
                  // guardar la copia en "Mi documentación" — se puede resubir a mano.
                }
              }
            }
          }
          toastUtils.dismiss(certToastId);

          const approvedNow = certOutcomes.filter(c => c.status === 'approved').map(c => c.service);
          const escalatedNow = certOutcomes.filter(c => c.status === 'rejected_escalated').map(c => c.service);

          if (escalatedNow.length > 0) {
            const existingRejected: string[] = (() => { try { return JSON.parse(localStorage.getItem(CERT_REJECTED_KEY) || '[]') as string[]; } catch { return []; } })();
            localStorage.setItem(CERT_REJECTED_KEY, JSON.stringify(Array.from(new Set([...existingRejected, ...escalatedNow]))));
          }

          setProfessionalData(prev => ({
            ...prev,
            services: prev.services.map(s => {
              if (approvedNow.includes(s.name)) return { ...s, active: true, certStatus: undefined } as ServiceItem;
              if (escalatedNow.includes(s.name)) return { ...s, active: false, certStatus: 'rejected_escalated' as const } as ServiceItem;
              return s;
            }),
          }));
          if (approvedNow.length > 0) {
            setOriginalProfile(prev => ({ ...prev, services: Array.from(new Set([...prev.services, ...approvedNow])) }));
            // Refresca "Mi documentación" para reflejar el certificado recién
            // subido sin que la psicóloga tenga que recargar la página.
            void loadFiles();
          }
          certValidation.resetAll(pendingCertServices);
        } catch (_certErr) {
          toastUtils.dismiss(certToastId);
          toastUtils.error('No se pudo activar el servicio validado');
          setSavingProfile(false);
          return;
        }
      }

      // 3b) Cambios en "Sobre tu atención" -> guardar + enviar notificación
      if (attentionChanged) {
        const serviceUiToApi: Record<string, string> = {
          'Individual': 'Individual Therapy',
          'Orientación vocacional': 'Vocational Counseling',
          'Orientación a padres': 'Orientación a padres',
          'Supervisión': 'Professional Supervision',
          'Material Digital': 'Downloadable',
          'Talleres/charlas': 'Talleres/charlas',
        };
        await apiCoreAdapter.updatePsychologistProfile({
          services: filteredServicesEs.map(s => serviceUiToApi[s] ?? s),
          // Keys = clinical_preferences.type (topic/specialization en
          // singular, no "topics"/"specializations") — ver comentario en
          // apiCoreAdapter.updatePsychologistProfile.
          preferences: {
            topic: currentTopics,
            specialization: currentSpecs,
            ...currentOtherByType,
          },
        });
        setOriginalProfile(prev => ({
          ...prev,
          services: filteredServicesEs,
          topics: currentTopicsUI,
          specializations: currentSpecsUI,
          // Agrupado por `type` — mismo criterio que la carga inicial
          // (GET /profile), no por filterKey (ver comentario en pendingOtherChanges).
          otherPreferences: Object.fromEntries(
            otherPreferenceGroups.filter(g => g.writable).map(g => [g.type, g.items.filter(i => i.active).map(i => i.name)]),
          ),
        }));
        activityTracker.trackAction('profile_attention_saved'); // Métricas de Actividad: se guardó Atención (servicios, temáticas, enfoques)
        // El email viejo de texto plano ("Modificación de perfil - Psi Platform") se eliminó;
        // la notificación va en el email combinado de consentimiento (sección 3c).
      }

      // 3c) Email combinado de consentimiento (cambios de atención + certificados + tope)
      if (attentionChanged || certOutcomes.length > 0 || patientLimitDirty) {
        const consentNow = new Date();
        const fechaConsentimiento =
          consentNow.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
          ' a las ' +
          consentNow.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

        // Bloque único por tipo (added/removed), agrupando todas las categorías juntas —
        // cada ítem lleva su categoría embebida en vez de una fila por categoría×tipo.
        const buildChangeBlock = (items: Array<{ category: string; name: string }>, type: 'added' | 'removed'): string => {
          if (!items.length) return '';
          const color = type === 'added' ? '#166534' : '#991b1b';
          const sign = type === 'added' ? '+' : '−';
          const label = type === 'added' ? 'Agregados' : 'Quitados';
          const list = items.map(i => `${i.category}: ${i.name}`).join(', ');
          return `<p style="margin:0 0 6px;font-size:14px;color:${color};font-family:Arial,Helvetica,sans-serif;"><strong>${label}:</strong> ${sign} ${list}</p>`;
        };

        const allAddedItems = [
          ...addedServices.map(name => ({ category: 'Servicios', name })),
          ...addedTopics.map(name => ({ category: 'Temáticas', name })),
          ...addedSpecs.map(name => ({ category: 'Enfoques', name })),
          ...otherAddedItems,
        ];
        const allRemovedItems = [
          ...removedServices.map(name => ({ category: 'Servicios', name })),
          ...removedTopics.map(name => ({ category: 'Temáticas', name })),
          ...removedSpecs.map(name => ({ category: 'Enfoques', name })),
          ...otherRemovedItems,
        ];

        // Fila de cambio de tope preferido
        const limitRow = (patientLimitDirty && processLimitPreferredDraft != null)
          ? `<p style="margin:0 0 6px;font-size:14px;color:#6D57BC;font-family:Arial,Helvetica,sans-serif;"><strong>Tope preferido de pacientes:</strong> ${processLimitPrefered ?? 'sin definir'} → ${processLimitPreferredDraft}</p>`
          : '';

        const changesDetail = [
          ...(attentionChanged ? [
            buildChangeBlock(allAddedItems, 'added'),
            buildChangeBlock(allRemovedItems, 'removed'),
          ] : []),
          limitRow,
        ].filter(Boolean).join('');

        const approvedOutcomes = certOutcomes.filter(c => c.status === 'approved');
        const escalatedOutcomes = certOutcomes.filter(c => c.status === 'rejected_escalated');
        const certSection = certOutcomes.length > 0 ? `
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F0FDF4;border-left:4px solid #16a34a;border-radius:0 8px 8px 0;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0 0 8px;font-size:12px;font-weight:700;color:#15803d;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Certificados validados automáticamente</p>
                    ${approvedOutcomes.length > 0 ? `<ul style="margin:0 0 8px;padding:0 0 0 16px;">${approvedOutcomes.map(c => `<li style="margin:0 0 4px;font-size:14px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">${c.service} — <em>${c.filename ?? ''}</em> → quedó activo de inmediato.</li>`).join('')}</ul>` : ''}
                    ${escalatedOutcomes.length > 0 ? `<ul style="margin:0;padding:0 0 0 16px;">${escalatedOutcomes.map(c => `<li style="margin:0 0 4px;font-size:14px;color:#991b1b;font-family:Arial,Helvetica,sans-serif;">${c.service} — <em>${c.filename ?? ''}</em> → no pudo validarse automáticamente. Si querés que revisemos tu caso, escribinos a administracion@psimammoliti.com.</li>`).join('')}</ul>` : ''}
                  </td>
                </tr>
              </table>` : '';

        const changesSection = changesDetail ? `
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Cambios aceptados</p>
                    ${changesDetail}
                  </td>
                </tr>
              </table>` : '';

        const combinedHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Confirmación de consentimiento — Modificación de perfil</title>
</head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
          <tr>
            <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
              <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
                Declaración de responsabilidad aceptada ✅
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:32px 36px 8px;">
              <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                Hola <strong>${firstName}</strong>, ¿cómo estás?
              </p>
              <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Te enviamos este mail para dejar registro de que el
                <strong>${fechaConsentimiento}</strong>
                aceptaste la declaración de responsabilidad por la modificación de tu perfil en Psi Platform.
              </p>
              ${changesSection}
              ${certSection}
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f7fb;border-radius:10px;margin-bottom:24px;">
                <tr>
                  <td style="padding:22px 24px;">
                    <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">
                      Declaración aceptada
                    </p>
                    <p style="margin:0;font-size:14px;color:#333333;line-height:1.7;font-family:Arial,Helvetica,sans-serif;">
                      Entiendo que las temáticas, servicios y enfoques que declaro son <strong>mi responsabilidad profesional</strong>. Solo he incluido aquellos en los que me siento realmente capacitada/o. Confirmo que he revisado estos cambios y asumo las consecuencias de cualquier información incorrecta o incompleta.
                    </p>
                  </td>
                </tr>
              </table>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0;font-size:13px;color:#92400E;line-height:1.6;font-style:italic;font-family:Arial,Helvetica,sans-serif;">
                      &quot;Asumo las consecuencias de cualquier información no fidedigna, tales como que mi asignación y permanencia en el equipo puedan verse afectadas.&quot;
                    </p>
                  </td>
                </tr>
              </table>
              <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Guardá este mail como constancia de tu aceptación. Si tenés alguna duda podés escribirnos a
                <a href="mailto:${contactChannel.email}" style="color:#7C3AED;font-weight:700;text-decoration:none;">${contactChannel.email}</a>.
              </p>
              <p style="margin:0 0 8px;font-size:14px;color:#555555;font-family:Arial,Helvetica,sans-serif;">
                ¡Que tengas lindo día! 😊
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 36px 28px;">
              <p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">
                Equipo Psi Mammoliti
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 16px;">
              <p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">
                Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a
                <a href="mailto:${contactChannel.email}" style="color:#888888;text-decoration:underline;">${contactChannel.email}</a>.
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 24px;">
              <hr style="border:none;border-top:1px solid #e8e8e8;margin:0 0 16px;" />
              <p style="margin:0;font-size:11px;color:#aaaaaa;text-align:center;font-family:Arial,Helvetica,sans-serif;">
                <a href="#" style="color:#aaaaaa;text-decoration:underline;">Click here to unsubscribe</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
        void apiCoreAdapter.psychologistNotifyProfileChanges({
          html: combinedHtml,
          subject: 'Confirmación de consentimiento — Modificación de perfil',
          from: 'no-responder@psimammoliti.com',
        });

        // Email adicional para OV si aplica
        if (addedServices.includes('Orientación vocacional')) {
          const now = new Date();
          const fechaConfirmacion =
            now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
            ' a las ' +
            now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
          const ovHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Confirmación — Servicio de Orientación Vocacional</title>
</head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
          <tr>
            <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
              <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
                Condiciones de Orientación Vocacional aceptadas ✅
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:32px 36px 8px;">
              <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                Hola <strong>${firstName}</strong>, ¿cómo estás?
              </p>
              <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Te enviamos este mail para dejar registro de que el
                <strong>${fechaConfirmacion}</strong>
                aceptaste las condiciones del servicio de <strong>Orientación Vocacional</strong> en Psi Platform.
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">
                      Condiciones aceptadas
                    </p>
                  </td>
                </tr>
              </table>
              <p style="margin:0 0 12px;font-size:15px;font-weight:700;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                Antes de continuar, leé con atención las condiciones del servicio de Orientación Vocacional.
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f7fb;border-radius:10px;margin-bottom:24px;">
                <tr>
                  <td style="padding:22px 24px;">
                    <p style="margin:0 0 16px;font-size:14px;color:#333333;line-height:1.7;font-family:Arial,Helvetica,sans-serif;">
                      El proceso de Orientación Vocacional tiene una duración de <strong>8 sesiones completas</strong>.
                      Los honorarios se liquidan únicamente al finalizar el proceso completo — no se abona por sesiones
                      individuales ni por procesos que no lleguen a completarse, independientemente del motivo.
                    </p>
                    <hr style="border:none;border-top:1px solid #e2e5f0;margin:0 0 16px;" />
                    <p style="margin:0;font-size:14px;color:#333333;line-height:1.7;font-family:Arial,Helvetica,sans-serif;">
                      Si en algún momento no podés continuar con un proceso en curso, es fundamental que nos lo comuniques
                      a la brevedad y colabores activamente en la derivación: compartiendo la historia clínica del paciente y facilitando
                      toda la información que el nuevo profesional necesite para darle continuidad al proceso de la manera
                      más ordenada posible.
                    </p>
                  </td>
                </tr>
              </table>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0;font-size:13px;color:#92400E;line-height:1.6;font-style:italic;font-family:Arial,Helvetica,sans-serif;">
                      "Asumo las consecuencias de cualquier información no fidedigna, tales como que mi asignación
                      y permanencia en el equipo puedan verse afectadas."
                    </p>
                  </td>
                </tr>
              </table>
              <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Guardá este mail como constancia de tu aceptación. Si tenés alguna duda podés escribirnos a
                <a href="mailto:administracion@psimammoliti.com" style="color:#7C3AED;font-weight:700;text-decoration:none;">administracion@psimammoliti.com</a>.
              </p>
              <p style="margin:0 0 8px;font-size:14px;color:#555555;font-family:Arial,Helvetica,sans-serif;">
                ¡Que tengas lindo día! 😊
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 36px 28px;">
              <p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">
                Equipo Psi Mammoliti
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 16px;">
              <p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">
                Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a
                <a href="mailto:administracion@psimammoliti.com" style="color:#888888;text-decoration:underline;">administracion@psimammoliti.com</a>.
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 24px;">
              <hr style="border:none;border-top:1px solid #e8e8e8;margin:0 0 16px;" />
              <p style="margin:0;font-size:11px;color:#aaaaaa;text-align:center;font-family:Arial,Helvetica,sans-serif;">
                <a href="#" style="color:#aaaaaa;text-decoration:underline;">Click here to unsubscribe</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
          // Este aviso va al EQUIPO (no a la psicóloga): son ellos los que tienen que
          // registrar que aceptó las condiciones de OV. El backend resuelve el
          // destinatario desde TEAM_NOTIFICATIONS_TO.
          void apiCoreAdapter.psychologistNotifyProfileChanges({
            html: ovHtml,
            subject: 'Condiciones de Orientación Vocacional aceptadas',
            from: 'no-responder@psimammoliti.com',
            audience: 'team',
          });
        }
      }

      setHasChanges(false);
      setShowSavePopup(false);
      setSavePopupStep(1);
      setConsentChecked(false);
      setCertFilesMap({});
      // Resetear flag para próximos guardados
      setCertRequestedThisFlow(false);
      setSavingProfile(false);
    } catch (_e) {
      // secureHttp already shows toast
      setShowSavePopup(false);
      setSavePopupStep(1);
      setSavingProfile(false);
    }
  };

  const continuEditing = () => {
    setShowSavePopup(false);
    setSavePopupStep(1);
    setConsentChecked(false);
    setCertFilesMap({});
  };

  return (
    <>
      <div className="w-full px-3 py-4 sm:p-8 min-h-screen">

        {/* Joyride Onboarding Perfil */}
        <Joyride
          key={profileTourKey}
          steps={profileSteps}
          run={runProfileTour}
          stepIndex={profileStepIndex}
          continuous
          showSkipButton={canSkipTour}
          scrollToFirstStep
          scrollOffset={300}
          disableOverlayClose
          disableCloseOnEsc
          spotlightClicks={false}
          disableScrolling={false}
          hideCloseButton
          styles={{
            options: { primaryColor: '#6D57BC', zIndex: 2000 },
            tooltip: { maxWidth: 540 },
            tooltipContent: { overflowWrap: 'anywhere', whiteSpace: 'normal' }
          }}
          floaterProps={{ offset: 12 }}
          locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Continuar', skip: 'Saltar' }}
          tooltipComponent={ProfileTooltipAny}
          callback={onProfileTourCallback} />

        {/* Joyride Onboarding Sobre tu atención */}
        <Joyride
          key={attentionTourKey}
          steps={attentionSteps}
          run={runAttentionTour}
          stepIndex={attentionStepIndex}
          continuous
          scrollToFirstStep
          scrollOffset={300}
          disableOverlayClose
          disableCloseOnEsc
          spotlightClicks={false}
          disableScrolling={false}
          hideCloseButton
          styles={{
            options: { primaryColor: '#6D57BC', zIndex: 2000 },
            tooltip: { maxWidth: 420 },
            tooltipContent: { overflowWrap: 'anywhere', whiteSpace: 'normal' }
          }}
          floaterProps={{ offset: 12 }}
          locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Continuar', skip: 'Saltar' }}
          tooltipComponent={AttentionTooltipAny}
          callback={onAttentionTourCallback} />

        {/* Joyride Onboarding Vacaciones y Días off */}
        <Joyride
          key={vacationsTourKey}
          steps={vacationsSteps}
          run={runVacationsTour}
          stepIndex={vacationsStepIndex}
          continuous
          scrollToFirstStep
          scrollOffset={300}
          disableOverlayClose
          disableCloseOnEsc
          spotlightClicks={false}
          disableScrolling={false}
          hideCloseButton
          styles={{
            // 2100: por encima del Dialog de shadcn (overlay z-[2000], contenido z-[2001]) —
            // el paso del modal demo (#vacation-tour-consequences) necesita que el tooltip y
            // el spotlight de Joyride se vean por encima del modal real que abre.
            options: { primaryColor: '#6D57BC', zIndex: 2100 },
            tooltip: { maxWidth: 420 },
            tooltipContent: { overflowWrap: 'anywhere', whiteSpace: 'normal' }
          }}
          floaterProps={{ offset: 12 }}
          locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Continuar', skip: 'Saltar' }}
          tooltipComponent={VacationsTooltipAny}
          callback={onVacationsTourCallback} />

        {/* Header card: avatar + greeting */}
        <div className="profile-card !top-0 !mb-3 !py-6" id="profile-personal-card">
          <div className="flex justify-center mb-3">
            <ProfileImage
              src={profileData.profileImage}
              name={profileData.name}
              lastName={profileData.lastName}
              sizeRem={4} />
          </div>
          <div className="text-center text-[1.25rem] font-medium" style={{ color: '#1e1b4b' }}>¡Hola, {profileData.name}!</div>
        </div>

        {/* Banner de bloqueo por vacaciones */}
        {(activeVacationEnd !== undefined && activeVacationEnd !== false) && (() => {
          // activeVacationEnd === null  → bloqueada sin fecha fin
          // activeVacationEnd === string → bloqueada con fecha fin
          // Si es null: activa pero sin to — mostrar sin fecha
          // Parsear solo la parte YYYY-MM-DD al mediodía local para evitar desfase por timezone
          const formatted = activeVacationEnd
            ? new Date(activeVacationEnd.substring(0, 10) + 'T12:00:00')
                .toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' })
            : null;
          return (
            <div className="mx-auto w-full max-w-[900px] mt-3 rounded-2xl border border-orange-300 px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4" style={{ background: 'rgba(255,237,213,0.5)' }}>
                {/* Icono */}
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                {/* Texto */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="text-xs sm:text-sm font-bold text-gray-800 m-0">Bloqueada para recibir pacientes nuevos</p>
                    <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-300 whitespace-nowrap">Vacaciones &amp; días off</span>
                  </div>
                  <p className="text-xs text-gray-600 m-0 leading-snug">
                    {formatted
                      ? <>No se te asignarán nuevos pacientes al menos hasta el <strong>{formatted}</strong>. </>
                      : <>No se te asignarán nuevos pacientes por el momento. </>}
                    {activeTab !== 'vacations' && <>Podés gestionar tus días desde la pestaña &quot;Vacaciones y Días off&quot;. </>}
                    <button type="button" onClick={() => setShowBlockModal(true)} className="underline text-orange-600 hover:text-orange-700 font-medium bg-transparent border-0 p-0 cursor-pointer">¿Cómo funciona el bloqueo?</button>
                  </p>
                </div>
                {/* Botón — oculto cuando ya está parada en "Vacaciones y Días off": el CTA sería redundante */}
                {activeTab !== 'vacations' && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('vacations')}
                    className="flex-shrink-0 inline-flex items-center gap-1 border border-orange-300 rounded-full px-3.5 py-1.5 text-xs font-semibold text-orange-600 bg-white hover:bg-orange-50 transition-colors"
                  >
                    Gestionar
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                )}
            </div>
          );
        })()}

        {/* Banner de bloqueo por calendario (Tope / Sin horarios / Situación personal) */}
        {activeBlockade && (() => {
          const reason = activeBlockade.reason as CalendarBlockadeReason;
          const info = CALENDAR_BLOCKADE_INFO[reason];
          const formattedTo = activeBlockade.to
            ? new Date(activeBlockade.to.substring(0, 10) + 'T12:00:00')
                .toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' })
            : null;
          const isTope = reason === 'Tope de Pacientes solicitado por psicologo';
          const title = 'Bloqueada para recibir pacientes nuevos';
          const description = isTope
            ? <>No se te asignarán nuevos pacientes por el momento ya que solicitaste un tope{processLimitPrefered !== null ? <> de {processLimitPrefered} procesos</> : ''}.</>
            : formattedTo
              ? <>No se te asignarán nuevos pacientes al menos hasta el <strong>{formattedTo}</strong>.</>
              : <>No se te asignarán nuevos pacientes por el momento.</>;
          const handleManage = () => {
            if (isTope) setActiveTab('attention');
            else if (reason === 'No cuenta con horarios de reprogramación') navigate('/calendar');
            else setShowContactoDirecto(true);
          };
          return (
            <div className="mx-auto w-full max-w-[900px] mt-3 rounded-2xl border border-orange-300 px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4" style={{ background: 'rgba(255,237,213,0.5)' }}>
              <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="text-xs sm:text-sm font-bold text-gray-800 m-0">{title}</p>
                  <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-300 whitespace-nowrap">{info.badgeLabel}</span>
                </div>
                <p className="text-xs text-gray-600 m-0 leading-snug">
                  {description}{' '}
                  <button type="button" onClick={() => setShowCalendarBlockModal(true)} className="underline text-orange-600 hover:text-orange-700 font-medium bg-transparent border-0 p-0 cursor-pointer">
                    ¿Cómo funciona este bloqueo?
                  </button>
                </p>
              </div>
              <button
                type="button"
                onClick={handleManage}
                className="flex-shrink-0 inline-flex items-center gap-1 border border-orange-300 rounded-full px-3.5 py-1.5 text-xs font-semibold text-orange-600 bg-white hover:bg-orange-50 transition-colors"
              >
                {info.manageLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          );
        })()}

        {/* Tabs card */}
        <div className="profile-card !top-0 mt-3 !mb-3" id="profile-tabs-card">
          {/* Tab navigation */}
          <div className="flex overflow-x-auto border-b border-gray-200 mb-6 scrollbar-hide">
            {([
              { key: 'general', label: 'Información general' },
              { key: 'attention', label: 'Sobre tu atención' },
              { key: 'vacations', label: 'Vacaciones y Días off' },
              { key: 'metrics', label: 'Tu estadía en Psi Mammoliti' },
              // Oculta por completo (ni el botón se renderiza) hasta que se confirme que
              // hay preguntas activas para esta psicóloga — ver therapeuticStyleAvailable.
              ...(therapeuticStyleAvailable
                ? [{ key: 'therapeuticStyle', label: 'Tu estilo terapéutico' } as const]
                : []),
              // Solo en la app mobile: la barra inferior no tiene "Notificaciones", vive acá.
              ...(isPwaApp ? [{ key: 'notifications', label: 'Notificaciones' } as const] : []),
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex-shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  activeTab === key
                    ? 'border-[#6D57BC] text-[#6D57BC]'
                    : 'border-transparent text-gray-500 hover:text-[#6D57BC]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'general' && (
            <ProfileGeneralTab
              profileData={profileData}
              joinDate={professionalData.joinDate}
              timezone={profileTimezone ?? psychologist?.timezone ?? null}
              onGuideReplay={() => { clearStep(STEP_KEYS.profile); setProfileTourKey(k => k + 1); setProfileStepIndex(0); setRunProfileTour(true); }}
              onMisDocumentosClick={() => setIsDocumentosModalOpen(true)}
              onTimezoneChangeClick={() => setIsTimezoneModalOpen(true)}
            />
          )}

          {activeTab === 'vacations' && (
            <ProfileVacationsTab
              userEmail={user?.email || ''}
              psychologistId={psychologistId || psychologist?.id || ''}
              onVacationSaved={fetchActiveVacationBanner}
              onGuideReplay={() => { setVacationsTourKey(k => k + 1); setVacationsStepIndex(0); setRunVacationsTour(true); }}
              tourShowDemoModal={runVacationsTour && vacationsStepIndex === 1}
            />
          )}

          {activeTab === 'attention' && (
            <ProfileAttentionTab
              professionalData={{
                ...professionalData,
                // Superpone el estado transitorio de validación IA (validating /
                // needs_additional_document) sobre el chip mientras el modal de guardado
                // está abierto — rejected_escalated ya viene persistido en professionalData.
                services: professionalData.services.map(s => {
                  const liveStatus = certValidation.states[s.name]?.status;
                  if (liveStatus === 'validating' || liveStatus === 'needs_additional_document') {
                    return { ...s, certStatus: liveStatus };
                  }
                  return s;
                }),
              }}
              toggleTag={toggleTag}
              toggleOtherPreference={toggleOtherPreference}
              pendingOtherChanges={pendingOtherChanges}
              servicesLoading={servicesLoading || false}
              loadingProfile={loadingProfile}
              preferencesCatalogLoading={preferencesCatalogLoading}
              otherPreferenceGroups={otherPreferenceGroups}
              dedicatedSectionLabels={dedicatedSectionLabels}
              onSave={handleSaveChanges}
              canSave={canSave}
              pendingAttentionChanges={pendingAttentionChanges}
              patientLimit={{
                processLimit: psychologist?.processLimit ?? null,
                draft: processLimitPreferredDraft ?? psychologist?.processLimit ?? null,
                currentAssignedProcesses,
                onChange: handlePatientLimitChange,
                onApplied: handlePatientLimitApplied,
                isModified: patientLimitDirty,
                psychologistId: psychologistId || psychologist?.id || null,
              }}
              hidePatientLimit={hidePatientLimitCard}
              onGuideReplay={() => { setAttentionTourKey(k => k + 1); setAttentionStepIndex(0); setRunAttentionTour(true); }}
            />
          )}

          {activeTab === 'metrics' && <ProfileMetricsTab />}

          {activeTab === 'notifications' && isPwaApp && <PwaNotificaciones embedded />}

          {activeTab === 'therapeuticStyle' && (
            <TherapeuticStyleTab
              state={therapeuticStyleState}
              loading={therapeuticStyleLoading}
              onAnswersSaved={handleTherapeuticStyleAnswersSaved}
            />
          )}
        </div>

        {/* Modal Mis Documentos */}
        <Dialog open={isDocumentosModalOpen} onOpenChange={(open) => { setIsDocumentosModalOpen(open); if (!open) setPreviewFile(null); }}>
          <DialogContent className="sm:max-w-lg w-full p-0 overflow-hidden">
            {/* Header con gradiente */}
            <div className="px-6 pt-6 pb-4 relative" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
              <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <X size={16} />
              </DialogClose>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {previewFile ? (
                    <div className="flex flex-col gap-1 w-full">
                      <button onClick={() => { setPreviewFile(null); setRenamingFile(null); }} className="shrink-0 inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 transition-colors w-fit">
                        <ChevronLeft size={18} />
                        <span className="text-sm font-medium">Volver</span>
                      </button>
                      <div className="min-w-0">
                        {renamingFile?.objectPath === previewFile.objectPath ? (
                          <div className="flex items-center gap-2">
                            <input
                              className="flex-1 text-sm font-medium text-gray-700 border border-purple-300 rounded-lg px-2 py-1 outline-none bg-white"
                              value={renamingFile.value}
                              onChange={e => setRenamingFile(r => r ? { ...r, value: e.target.value } : null)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleRenameFile();
                                if (e.key === 'Escape') setRenamingFile(null);
                              }}
                              autoFocus
                              disabled={renamingLoading}
                            />
                            <Button variant="ghost" size="sm" onClick={handleRenameFile} disabled={renamingLoading} className="h-7 px-2 text-green-600 hover:text-green-700 shrink-0">✓</Button>
                            <Button variant="ghost" size="sm" onClick={() => setRenamingFile(null)} disabled={renamingLoading} className="h-7 px-2 text-gray-400 hover:text-gray-600 shrink-0">✕</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group/title">
                            <span className="text-sm font-semibold truncate" style={{ color: '#1e1b4b' }}>{previewFile.name}</span>
                            <button
                              onClick={() => setRenamingFile({ objectPath: previewFile.objectPath, value: previewFile.name })}
                              className="shrink-0 opacity-0 group-hover/title:opacity-100 transition-opacity text-gray-300 hover:text-purple-500"
                              title="Renombrar"
                            >
                              <Pencil size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>
                        <FileText size={18} className="text-white" />
                      </div>
                      <div>
                        <span className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>Mi documentación</span>
                        {myFiles.filter(f => f.folder === 'documentacion' && !f.name.toLowerCase().includes('liq')).length > 0 && (
                          <span className="ml-2 text-xs font-medium bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                            {myFiles.filter(f => f.folder === 'documentacion' && !f.name.toLowerCase().includes('liq')).length}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </DialogTitle>
              </DialogHeader>
            </div>

            {/* Contenido */}
            <div className="px-6 pb-6">
              {previewFile ? (
                <div className="flex flex-col gap-3">
                  <div className="relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50" style={{ height: '420px' }}>
                    {/\.(png|jpe?g|gif|webp|svg)$/i.test(previewFile.name) ? (
                      <img src={previewFile.publicUrl} alt={previewFile.name} className="w-full h-full object-contain" />
                    ) : (
                      <>
                        <div id="preview-loader" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-50 z-10">
                          <CircularProgress />
                          <span className="text-xs text-gray-400">Cargando documento…</span>
                        </div>
                        <iframe
                          src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewFile.publicUrl)}&embedded=true`}
                          className="w-full h-full border-0"
                          title={previewFile.name}
                          onLoad={() => {
                            const loader = document.getElementById('preview-loader');
                            if (loader) loader.style.display = 'none';
                          }}
                        />
                      </>
                    )}
                  </div>
                  <a href={previewFile.publicUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-2 text-xs text-purple-500 hover:text-purple-700 transition-colors">
                    <ArrowUpRight size={13} /> Abrir en nueva pestaña
                  </a>
                </div>
              ) : loadingFiles ? (
                <div className="flex justify-center py-10"><CircularProgress /></div>
              ) : myFiles.filter(f => f.folder === 'documentacion' && !f.name.toLowerCase().includes('liq')).length === 0 ? (
                <div className="flex flex-col items-center py-10 text-gray-400">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                    <FileText size={32} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No hay archivos todavía</p>
                  <p className="text-xs text-gray-400 mt-1">Subí tu primer documento</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[360px] overflow-y-auto py-1">
                  {myFiles.filter(f => f.folder === 'documentacion' && !f.name.toLowerCase().includes('liq')).map((file, i) => (
                    <div
                      key={i}
                      onClick={() => !renamingFile && setPreviewFile(file)}
                      className="group relative flex flex-col items-center gap-2 p-3 rounded-2xl bg-gray-50 border border-transparent hover:border-purple-200 hover:bg-purple-50/50 transition-all duration-150 cursor-pointer"
                    >
                      {renamingFile?.objectPath === file.objectPath ? (
                        <input
                          className="w-full text-xs text-center text-gray-700 border border-purple-300 rounded-lg px-1 py-0.5 outline-none bg-white mt-10"
                          value={renamingFile.value}
                          onChange={e => setRenamingFile(r => r ? { ...r, value: e.target.value } : null)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameFile();
                            if (e.key === 'Escape') setRenamingFile(null);
                          }}
                          autoFocus
                          disabled={renamingLoading}
                        />
                      ) : (
                        <>
                          {/* Ícono */}
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>
                            <FileText size={22} className="text-purple-500" />
                          </div>
                          {/* Nombre + botón editar */}
                          <div className="flex items-center justify-center gap-1 w-full">
                            <span className="text-xs font-medium text-gray-600 text-center leading-tight line-clamp-2 break-all">{file.name}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setRenamingFile({ objectPath: file.objectPath, value: file.name }); }}
                              className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-purple-500"
                              title="Renombrar"
                            >
                              <Pencil size={11} />
                            </button>
                          </div>
                        </>
                      )}
                      {renamingFile?.objectPath === file.objectPath && (
                        <div className="flex gap-1 mt-1">
                          <Button variant="ghost" size="sm" onClick={handleRenameFile} disabled={renamingLoading} className="h-6 px-1.5 text-xs text-green-600 hover:text-green-700">✓</Button>
                          <Button variant="ghost" size="sm" onClick={() => setRenamingFile(null)} disabled={renamingLoading} className="h-6 px-1.5 text-xs text-gray-400 hover:text-gray-600">✕</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button — solo en grid view */}
              {!previewFile && <div className="mt-4 pt-4 border-t border-gray-100">
                <label className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all duration-200 ${uploadingDoc ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-white hover:opacity-90'}`}
                  style={uploadingDoc ? {} : { background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    disabled={uploadingDoc}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDocUpload(file);
                      e.target.value = '';
                    }} />
                  <Upload size={15} />
                  {uploadingDoc ? 'Subiendo…' : 'Subir archivo'}
                </label>
              </div>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Popup de confirmación — 2 pasos */}
        <Dialog open={showSavePopup} onOpenChange={(open) => { if (!open) { setShowSavePopup(false); setSavePopupStep(1); setConsentChecked(false); setCertFilesMap({}); setPendingCertServices([]); } }}>
          <DialogContent className="sm:max-w-lg w-full p-0 overflow-hidden max-h-[90dvh] flex flex-col">

            {/* ── PASO 1: Diff visual ── */}
            {savePopupStep === 1 && (
              <>
                <div className="px-6 pt-6 pb-4 relative flex-shrink-0" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
                  <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    <X size={16} />
                  </DialogClose>
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>
                      Revisá tus cambios
                    </DialogTitle>
                  </DialogHeader>
                </div>

                <div className="px-6 pb-4 flex-1 overflow-y-auto min-h-0 flex flex-col gap-5">
                  {(() => {
                    const addedServicesSet = new Set(changesDiff.services.added);
                    const removedServicesSet = new Set(changesDiff.services.removed);
                    const addedThemesSet = new Set(changesDiff.themes.added.map(n => normalizeForCompare(n)));
                    const removedThemesSet = new Set(changesDiff.themes.removed.map(n => normalizeForCompare(n)));
                    const addedApproachesSet = new Set(changesDiff.approaches.added.map(n => normalizeForCompare(n)));
                    const removedApproachesSet = new Set(changesDiff.approaches.removed.map(n => normalizeForCompare(n)));

                    // Before/after full lists
                    const beforeServices = (originalProfile.services || []);
                    const afterServices = professionalData.services.filter(s => s.active).map(s => s.name);
                    const beforeThemes = (originalProfile.topics || []).map(t => apiLabelToUi(t, professionalData.themes));
                    const afterThemes = professionalData.themes.filter(t => t.active).map(t => t.name);
                    const beforeApproaches = (originalProfile.specializations || []).map(s => apiLabelToUi(s, professionalData.approaches));
                    const afterApproaches = professionalData.approaches.filter(a => a.active).map(a => a.name);

                    const sections = [
                      {
                        label: 'Servicios',
                        before: beforeServices, after: afterServices,
                        isAdded: (n: string) => addedServicesSet.has(n),
                        isRemoved: (n: string) => removedServicesSet.has(n),
                        hasChanges: changesDiff.services.added.length > 0 || changesDiff.services.removed.length > 0,
                      },
                      {
                        label: 'Temáticas',
                        before: beforeThemes, after: afterThemes,
                        isAdded: (n: string) => addedThemesSet.has(normalizeForCompare(n)),
                        isRemoved: (n: string) => removedThemesSet.has(normalizeForCompare(n)),
                        hasChanges: changesDiff.themes.added.length > 0 || changesDiff.themes.removed.length > 0,
                      },
                      {
                        label: 'Enfoques',
                        before: beforeApproaches, after: afterApproaches,
                        isAdded: (n: string) => addedApproachesSet.has(normalizeForCompare(n)),
                        isRemoved: (n: string) => removedApproachesSet.has(normalizeForCompare(n)),
                        hasChanges: changesDiff.approaches.added.length > 0 || changesDiff.approaches.removed.length > 0,
                      },
                      ...otherPreferenceGroups.filter(g => g.writable).map(group => {
                        const diff = changesDiff.other[group.filterKey] ?? { added: [], removed: [] };
                        const addedSet = new Set(diff.added.map(n => normalizeForCompare(n)));
                        const removedSet = new Set(diff.removed.map(n => normalizeForCompare(n)));
                        return {
                          label: group.label,
                          before: originalProfile.otherPreferences[group.type] || [],
                          after: group.items.filter(i => i.active).map(i => i.name),
                          isAdded: (n: string) => addedSet.has(normalizeForCompare(n)),
                          isRemoved: (n: string) => removedSet.has(normalizeForCompare(n)),
                          hasChanges: diff.added.length > 0 || diff.removed.length > 0,
                        };
                      }),
                    ].filter(s => s.hasChanges);

                    const Pill = ({ name, variant }: { name: string; variant: 'added' | 'removed' | 'neutral' }) => (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-normal break-words ${
                        variant === 'added' ? 'bg-green-50 text-green-700 border-green-200' :
                        variant === 'removed' ? 'bg-red-50 text-red-400 border-red-200 line-through opacity-60' :
                        'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {variant === 'added' && <span className="font-bold text-green-600">+</span>}
                        {variant === 'removed' && <span className="font-bold text-red-400" style={{ textDecoration: 'none' }}>−</span>}
                        {name}
                      </span>
                    );

                    const tagSections = sections.map(({ label, before, after, isAdded, isRemoved }) => (
                      <div key={label} className="rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0 flex flex-col max-h-64">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
                        </div>
                        {/* Contenido de la sección con scroll propio — evita que una sección con
                            muchos cambios (ej. Temáticas) empuje al resto fuera del alto máximo. */}
                        <div className="grid grid-cols-2 divide-x divide-gray-100 overflow-y-auto min-h-0">
                          {/* Antes — todos neutral */}
                          <div className="px-4 py-3">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Antes</p>
                            <div className="flex flex-wrap gap-1.5">
                              {before.length === 0
                                ? <span className="text-xs text-gray-300 italic">Ninguno</span>
                                : before.map(name => (
                                  <Pill key={name} name={name} variant="neutral" />
                                ))
                              }
                            </div>
                          </div>
                          {/* Ahora — mismo orden que Antes: neutral si sigue, tachado si se quitó; añadidos al final en verde */}
                          <div className="px-4 py-3">
                            <p className="text-[10px] font-semibold text-[#6D57BC] uppercase tracking-wider mb-2">Ahora</p>
                            <div className="flex flex-wrap gap-1.5">
                              {before.map(name => (
                                <Pill key={name} name={name} variant={isRemoved(name) ? 'removed' : 'neutral'} />
                              ))}
                              {after.filter(n => isAdded(n)).map(name => (
                                <Pill key={`add-${name}`} name={name} variant="added" />
                              ))}
                              {before.length === 0 && after.filter(n => isAdded(n)).length === 0 && (
                                <span className="text-xs text-gray-300 italic">Ninguno</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ));

                    const limitChanged = changesDiff.patientLimit.to !== null
                      && changesDiff.patientLimit.to !== changesDiff.patientLimit.from;
                    const limitSection = limitChanged ? (
                      <div key="patient-limit" className="rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Tope preferido de pacientes</p>
                        </div>
                        <div className="grid grid-cols-2 divide-x divide-gray-100">
                          <div className="px-4 py-3">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Antes</p>
                            <p className="text-lg font-semibold text-gray-500 m-0">
                              {changesDiff.patientLimit.from ?? <span className="text-xs italic text-gray-300">Sin definir</span>}
                            </p>
                          </div>
                          <div className="px-4 py-3">
                            <p className="text-[10px] font-semibold text-[#6D57BC] uppercase tracking-wider mb-2">Ahora</p>
                            <p className="text-lg font-semibold text-[#6D57BC] m-0">
                              {changesDiff.patientLimit.to}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null;

                    return <>{tagSections}{limitSection}</>;
                  })()}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 flex-shrink-0">
                  <button
                    type="button"
                    className="btn-purple w-full"
                    onClick={() => setSavePopupStep(onlyPatientLimitChange ? 3 : 2)}
                  >
                    Continuar
                  </button>
                  <button
                    type="button"
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
                    onClick={continuEditing}
                  >
                    Volver a editar
                  </button>
                </div>
              </>
            )}

            {/* ── PASO 2: Declaración + certificados ── */}
            {savePopupStep === 2 && (
              <>
                <div className="px-6 pt-6 pb-4 relative flex-shrink-0" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
                  <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    <X size={16} />
                  </DialogClose>
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>
                      {pendingCertServices.length > 0 ? 'Declaración y certificados' : 'Declaración de responsabilidad'}
                    </DialogTitle>
                  </DialogHeader>
                </div>

                <div className="px-6 pb-4 flex-1 overflow-y-auto min-h-0 flex flex-col gap-4">
                  <div className="rounded-2xl bg-purple-50 border border-purple-100 px-5 py-4">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Entiendo que las temáticas, servicios y enfoques que declaro son <strong>mi responsabilidad profesional</strong>. Solo he incluido aquellos en los que me siento realmente capacitada/o. Confirmo que he revisado estos cambios y asumo las consecuencias de cualquier información incorrecta o incompleta.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
                    <p className="text-xs text-amber-700 leading-relaxed italic">
                      &quot;Asumo las consecuencias de cualquier información no fidedigna, tales como que mi asignación y permanencia en el equipo puedan verse afectadas.&quot;
                    </p>
                  </div>

                  {/* Certificados requeridos por servicio — validación IA en el momento de subir */}
                  {pendingCertServices.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Certificados requeridos</p>
                      {pendingCertServices.map(service => {
                        const certState = certValidation.states[service];
                        return (
                        <div key={service} className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex flex-col gap-2">
                          <p className="text-xs font-semibold text-amber-800">
                            Servicio: <strong>{service}</strong>
                          </p>
                          <p className="text-xs text-amber-700 leading-relaxed">
                            Para ofrecer el servicio de <strong>{service}</strong>, validamos automáticamente que el certificado corresponda a esta especialidad.
                          </p>
                          <input
                            id={`cert-upload-${service}`}
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={(e) => {
                              const f = e.target.files?.[0] ?? null;
                              setCertFilesMap(prev => ({ ...prev, [service]: f }));
                              setCertAdditionalFilesMap(prev => ({ ...prev, [service]: null }));
                              if (f) {
                                certValidation.validateCertificate(service, f).catch(() => { /* error visible via certState.error */ });
                              }
                            }}
                          />
                          {certFilesMap[service] && (
                            <span className="text-xs text-green-700 font-medium">&#10003; {certFilesMap[service]!.name}</span>
                          )}
                          {certState?.error && (
                            <span className="text-xs text-red-600">{certState.error}</span>
                          )}
                          {certState && certState.status !== 'idle' && (
                            <ValidationResultPanel
                              status={certState.status === 'validating' ? 'validating' : certState.status}
                              userFeedback={certState.result?.userFeedback}
                              additionalDocumentInputId={`cert-additional-${service}`}
                              additionalDocumentFileName={certAdditionalFilesMap[service]?.name}
                              onAdditionalDocumentSelected={(file) => {
                                setCertAdditionalFilesMap(prev => ({ ...prev, [service]: file }));
                                const mainFile = certFilesMap[service];
                                if (mainFile) {
                                  certValidation.submitAdditionalDocument(service, mainFile, file)
                                    .catch(() => { /* error visible via certState.error */ });
                                }
                              }}
                            />
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 flex-shrink-0">
                  <button
                    type="button"
                    className="btn-purple w-full"
                    onClick={() => setSavePopupStep(3)}
                    disabled={pendingCertServices.some(s => {
                      const st = certValidation.states[s]?.status;
                      return st !== 'approved' && st !== 'rejected_escalated';
                    })}
                  >
                    De acuerdo, continuar
                  </button>
                  <button
                    type="button"
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
                    onClick={() => {
                      setSavePopupStep(1);
                      setCertFilesMap({});
                      setCertAdditionalFilesMap({});
                      certValidation.resetAll(pendingCertServices);
                    }}
                  >
                    Volver
                  </button>
                </div>
              </>
            )}

            {/* ── PASO 3: Confirmación final ── */}
            {savePopupStep === 3 && (
              <>
                <div className="px-6 pt-6 pb-4 relative flex-shrink-0" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
                  <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    <X size={16} />
                  </DialogClose>
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>
                      Confirmación final
                    </DialogTitle>
                    <p className="text-sm text-gray-500 mt-1">Revisá el resumen de todos tus cambios antes de confirmar.</p>
                  </DialogHeader>
                </div>

                <div className="px-6 pb-4 flex-1 overflow-y-auto min-h-0 flex flex-col gap-4">
                  {/* Resumen compacto de cambios */}
                  {(() => {
                    const rows: React.ReactNode[] = [];

                    const addedS = changesDiff.services.added;
                    const removedS = changesDiff.services.removed;
                    const addedT = changesDiff.themes.added;
                    const removedT = changesDiff.themes.removed;
                    const addedA = changesDiff.approaches.added;
                    const removedA = changesDiff.approaches.removed;
                    const limitFrom = changesDiff.patientLimit.from;
                    const limitTo = changesDiff.patientLimit.to;
                    const limitChanged = limitTo !== null && limitTo !== limitFrom;

                    // Un solo bloque por tipo (added/removed) agrupando las 3 categorías juntas —
                    // cada chip lleva su categoría embebida en vez de una fila por categoría×tipo.
                    const ChangeRow = ({ items, type }: { items: Array<{ category: string; name: string }>; type: 'added' | 'removed' }) => (
                      <div className="flex flex-wrap items-start gap-1.5">
                        <span className={`text-xs font-semibold shrink-0 ${type === 'added' ? 'text-green-700' : 'text-red-500'}`}>
                          {type === 'added' ? 'Agregados' : 'Quitados'}:
                        </span>
                        {items.map(({ category, name }) => (
                          <span key={`${category}-${name}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-normal break-words ${
                            type === 'added' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-400 border-red-200 line-through opacity-70'
                          }`}>
                            {type === 'added' ? '+' : '−'} {category}: {name}
                          </span>
                        ))}
                      </div>
                    );

                    const otherAdded = Object.values(changesDiff.other).flatMap(
                      d => d.added.map(name => ({ category: d.label, name })),
                    );
                    const otherRemoved = Object.values(changesDiff.other).flatMap(
                      d => d.removed.map(name => ({ category: d.label, name })),
                    );

                    const allAdded = [
                      ...addedS.map(name => ({ category: 'Servicios', name })),
                      ...addedT.map(name => ({ category: 'Temáticas', name })),
                      ...addedA.map(name => ({ category: 'Enfoques', name })),
                      ...otherAdded,
                    ];
                    const allRemoved = [
                      ...removedS.map(name => ({ category: 'Servicios', name })),
                      ...removedT.map(name => ({ category: 'Temáticas', name })),
                      ...removedA.map(name => ({ category: 'Enfoques', name })),
                      ...otherRemoved,
                    ];

                    if (allAdded.length) rows.push(<ChangeRow key="added" items={allAdded} type="added" />);
                    if (allRemoved.length) rows.push(<ChangeRow key="removed" items={allRemoved} type="removed" />);
                    if (limitChanged) rows.push(
                      <div key="limit" className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-[#6D57BC] shrink-0">Tope de pacientes:</span>
                        <span className="text-xs text-gray-400 line-through">{limitFrom ?? '—'}</span>
                        <span className="text-xs text-gray-400">→</span>
                        <span className="text-xs font-semibold text-[#6D57BC]">{limitTo}</span>
                      </div>
                    );
                    if (pendingCertServices.length > 0) rows.push(
                      <div key="certs" className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-green-700">Certificados validados por IA:</span>
                        {pendingCertServices.map(s => {
                          const st = certValidation.states[s]?.status;
                          return (
                            <span key={s} className="text-xs text-gray-600">
                              <strong>{s}</strong>{certFilesMap[s] ? ` — ${certFilesMap[s]!.name}` : ''}
                              {st === 'approved' ? ' → aprobado' : st === 'rejected_escalated' ? ' → en revisión' : ''}
                            </span>
                          );
                        })}
                      </div>
                    );

                    if (rows.length === 0) return null;

                    return (
                      <div className="rounded-2xl border border-gray-100 px-5 py-4 flex flex-col gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Resumen de cambios</p>
                        {rows}
                      </div>
                    );
                  })()}

                  <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
                    <p className="text-xs text-amber-700 leading-relaxed italic">
                      &quot;Asumo las consecuencias de cualquier información no fidedigna, tales como que mi asignación y permanencia en el equipo puedan verse afectadas.&quot;
                    </p>
                  </div>

                  <label htmlFor="consent-checkbox" className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      id="consent-checkbox"
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-purple-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Estoy de acuerdo con lo informado arriba</span>
                  </label>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 flex-shrink-0">
                  <button
                    type="button"
                    className="btn-purple w-full"
                    onClick={confirmSave}
                    disabled={savingProfile || !consentChecked}
                  >
                    {savingProfile ? 'Guardando…' : 'Confirmar cambios'}
                  </button>
                  <button
                    type="button"
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
                    onClick={() => { setSavePopupStep(onlyPatientLimitChange ? 1 : 2); setConsentChecked(false); }}
                  >
                    Volver
                  </button>
                </div>
              </>
            )}

          </DialogContent>
        </Dialog>

      </div>

        {/* Modal ¿Cómo funciona el bloqueo? */}
        <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
          <DialogContent className="sm:max-w-2xl w-full p-0 overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <DialogHeader>
                <DialogTitle className="text-base sm:text-lg">¿Qué pasa con la agenda para pacientes nuevos?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-gray-600 mt-2 mb-4">
                Cada vez que pedís días off, se ajusta tu agenda para nuevas asignaciones, según la duración de tu descanso:
              </p>
              {(() => {
                const activeRow = getVacationBracketIndex(activeVacationDays);
                const rowBase = 'divide-y divide-gray-100';
                const highlight = 'bg-[#F0EEFB]';
                return (
                  <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left font-semibold text-gray-700 px-4 py-3 border-b border-gray-200">{VACATION_BLOCK_TABLE_HEADERS.days}</th>
                          <th className="text-left font-semibold text-gray-700 px-4 py-3 border-b border-gray-200">{VACATION_BLOCK_TABLE_HEADERS.before}</th>
                          <th className="text-left font-semibold text-gray-700 px-4 py-3 border-b border-gray-200">{VACATION_BLOCK_TABLE_HEADERS.after}</th>
                        </tr>
                      </thead>
                      <tbody className={rowBase}>
                        {VACATION_BLOCK_BRACKETS.map((bracket, i) => {
                          const isActive = activeRow === i;
                          return (
                            <tr key={bracket.key} className={isActive ? highlight : undefined}>
                              <td className={`px-4 py-3 ${isActive ? 'text-[#5a47a8] font-semibold' : 'text-gray-700'}`}>
                                <div className="flex items-start gap-2">
                                  {isActive && <span className="mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#6D57BC] inline-block" />}
                                  <span>{bracket.label}</span>
                                </div>
                              </td>
                              <td className={`px-4 py-3 ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockBeforeLabel}</td>
                              <td className={`px-4 py-3 ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockAfterLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              <div className="flex flex-col gap-2 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider m-0">Ejemplos</p>
                <ul className="flex flex-col gap-1.5 pl-4 m-0 list-disc text-sm text-gray-600">
                  {VACATION_BLOCK_BRACKETS.map((bracket) => (
                    <li key={bracket.key}>{VACATION_BLOCK_ILLUSTRATIVE_EXAMPLES[bracket.key]}</li>
                  ))}
                </ul>
              </div>
              {activeVacationDays !== null && (
                <p className="text-xs text-[#6D57BC] mb-4 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6D57BC] flex-shrink-0 inline-block" />
                  Tu caso actual ({activeVacationDays} días) corresponde a la fila resaltada.
                </p>
              )}
            </div>
            <div className="flex justify-end px-6 pb-5">
              <Button onClick={() => setShowBlockModal(false)} className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]">
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal "¿Cómo funciona este bloqueo?" — Tope / Sin horarios / Situación personal */}
        <Dialog open={showCalendarBlockModal} onOpenChange={setShowCalendarBlockModal}>
          <DialogContent className="sm:max-w-lg w-full p-0 overflow-hidden">
            {activeBlockade && (() => {
              const reason = activeBlockade.reason as CalendarBlockadeReason;
              const info = CALENDAR_BLOCKADE_INFO[reason];
              const isTope = reason === 'Tope de Pacientes solicitado por psicologo';
              const isSinHorarios = reason === 'No cuenta con horarios de reprogramación';
              const formattedTo = activeBlockade.to
                ? new Date(activeBlockade.to.substring(0, 10) + 'T12:00:00')
                    .toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' })
                : null;
              const yourCase = isTope
                ? <>Tu límite preferido es de <strong>{processLimitPrefered ?? '—'} procesos</strong>, y ya lo alcanzaste.</>
                : formattedTo
                  ? <>Este bloqueo está activo hasta el <strong>{formattedTo}</strong>.</>
                  : <>Este bloqueo está activo por el momento, sin una fecha de fin definida todavía.</>;
              const handleManageFromModal = () => {
                setShowCalendarBlockModal(false);
                if (isTope) setActiveTab('attention');
                else if (isSinHorarios) navigate('/calendar');
                else setShowContactoDirecto(true);
              };
              return (
                <div className="px-6 pt-6 pb-5 flex flex-col gap-4">
                  <DialogHeader>
                    <DialogTitle className="text-base sm:text-lg">¿Cómo funciona este bloqueo?</DialogTitle>
                  </DialogHeader>
                  <span className="w-fit text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-600 border border-orange-300">
                    {info.badgeLabel}
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cómo funciona</p>
                    <p className="text-sm text-gray-600 leading-relaxed m-0">{info.howItWorks}</p>
                  </div>
                  <div className="rounded-xl bg-[#F0EEFB] px-4 py-3">
                    <p className="text-xs font-semibold text-[#5a47a8] uppercase tracking-wider mb-1.5">Tu caso</p>
                    <p className="text-sm text-[#5a47a8] leading-relaxed m-0">{yourCase}</p>
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={() => setShowCalendarBlockModal(false)}>Cerrar</Button>
                    <Button onClick={handleManageFromModal} className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]">
                      {info.manageLabel}
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        <ContactoDirectoModal open={showContactoDirecto} onOpenChange={setShowContactoDirecto} />

        <OVConditionsModal
          open={showOVModal}
          onConfirm={handleOVConfirm}
          onCancel={handleOVCancel}
        />

        <TimezoneModal
          open={isTimezoneModalOpen}
          onClose={() => setIsTimezoneModalOpen(false)}
          currentTimezone={profileTimezone ?? psychologist?.timezone ?? null}
          onSaved={(newTz) => {
            const prevTz = profileTimezone ?? psychologist?.timezone ?? null;
            setProfileTimezone(newTz);
            setIsTimezoneModalOpen(false);
            try { localStorage.setItem('psi-timezone', newTz); } catch { /* noop */ }
            if (psychologist) setPsychologist({ ...psychologist, timezone: newTz });
            const psiId = psychologistId || psychologist?.id;
            if (psiId) loadPsychologist(psiId).catch(() => { /* silencioso */ });

            if (newTz !== prevTz) {
              // Todo lo cacheado (disponibilidad, bloqueos, sesiones) quedó calculado
              // con el huso horario anterior — invalidar todo antes de que Agenda
              // vuelva a pedirlo, para que no haga falta un refresh manual.
              invalidateSchedulesCache();
              invalidateSessionsCache();
              // BA_TIMEZONE como fallback: si nunca tuvo un huso configurado, es el
              // default que asume el resto de la app (ver SessionItem, WeeklyPsychologistCalendar).
              markTimezoneAgendaBannerPending(prevTz ?? BA_TIMEZONE, newTz);
              navigate('/calendar');
            }
          }}
        />
      </>
  );
};

export default PsychologistProfile;
